// src/utils/summary.js
import { getFollowUpState } from "./followUp";
import { buildStepAlignedFollowUp, inferCurrentStep } from "./stepRouter";
import { buildCoachingState } from "./buildCoachingState";
import { isSchedulingOnlySession } from "./schedulingContext";
import summaryTextIntegrity from "./summaryTextIntegrity";
import { hasExplicitWorkloadStructureSignal } from "./workloadStructure";
import {
  detectCumulativeSafetyAdjacentSignal,
  getSafetyContextState,
  getSafetyContextWindow,
  hasActivePersonalSafetySignal,
  hasExplicitSafetyDeescalation,
  hasPracticalContextClarification,
} from "./safetyContext";

const operationalIntent = require("./operationalIntent");
const {
  maskQuotedParticipantSpeech,
} = require("./participantEvidence");
const {
  classifyFactualPlanningIntent,
  classifyOperationalIntent,
} = operationalIntent;
const {
  isLikelyLegacyTruncatedSummary,
  preserveCanonicalSummaryText,
} = summaryTextIntegrity;

const SUMMARY_API_PATH = "/api/session-summary";
const LOW_CONTEXT_SUMMARY_TEXT = "Summary will appear once this session has more context.";
const LOW_CONTEXT_NEXT_STEP = "Continue the current conversation until there is enough context to summarise.";
const INTERNAL_FAIL_CLOSED_TEXT_PATTERN =
  /\b(?:unsupported themes?|current[-\s]session transcript|topic[-\s]specific recap|generated summary contained themes?|without carrying forward)\b/i;
const WEBSITE_QUALITY_CHECK_ACTION = "Open the website, check HTTPS, test the contact form, and write down any problem before moving to another task.";
const WEBSITE_QUALITY_CHECK_OBJECTIVE = "Complete a website quality check as the first work priority.";
const WEBSITE_QUALITY_CHECK_FOLLOW_UP = "Review any problem recorded during the website quality check.";
const SUMMARY_AUDIT_FIELDS = [
  "generationStatus",
  "status",
  "isCurrent",
  "sourceMessageCount",
  "sourceLastMessageAt",
  "sourceLatestMessageAt",
  "sourceTranscriptFingerprint",
  "sourceFollowUpFingerprint",
  "sourceTopic",
  "staleReason",
  "staleDetectedAt",
  "supersededAt",
  "supersededReason",
  "persistenceStatus",
  "persistedAt",
  "persistenceFailedAt",
  "persistenceError",
  "persistenceUpdatedAt",
];
const THEME_KEYWORDS = [
  { label: "burnout", pattern: /\bburn(?:t|ed)\s*out|burnout\b/i },
  { label: "overwhelm", pattern: /\boverwhelm(?:ed|ing)?\b|\btoo much\b|\boverload(?:ed)?\b/i },
  { label: "avoidance", pattern: /\bavoid(?:ing|ance)?\b|\bprocrastinat(?:e|ing|ion)\b|\bput(?:ting)? off\b|\bstuck\b|\bcan't start\b|\bcannot start\b/i },
  { label: "exhaustion", pattern: /\bexhaust(?:ed|ion)?\b|\btired\b|\bfatigue(?:d)?\b|\bdrained\b|\blow energy\b|\benergy crash(?:es|ed)?\b/i },
  { label: "frustration", pattern: /\bfrustrat(?:ed|ing|ion)?\b|\birritat(?:ed|ing|ion)?\b|\bannoyed\b|\bangry\b/i },
  { label: "emotional suppression", pattern: /\bshut down\b|\bnumb\b|\bflat\b|\bpush(?:ing)? through\b|\bhold(?:ing)? it in\b|\bsuppress(?:ing|ed)?\b/i },
  { label: "stress", pattern: /\bstress(ed)?\b|\bpressure\b/i },
  { label: "anxiety", pattern: /\banxious|anxiety|panic|worried|fear\b/i },
  { label: "focus", pattern: /\bfocus|distract(ed|ion)?|attention|concentrat(e|ion)\b/i },
  { label: "energy", pattern: /\benergy\b/i },
  { label: "work", pattern: /\bwork|job|meeting|deadline|boss|career\b/i },
  { label: "relationships", pattern: /\bpartner|family|friend|relationship|dating\b/i },
  { label: "confidence", pattern: /\bconfidence|self-worth|self doubt|imposter\b/i },
  { label: "routine", pattern: /\broutine|habit|sleep|schedule|consistency\b/i },
  { label: "caregiving pressure", pattern: /\bcaregiv(?:e|ing|er)|look(?:ing)? after|taking care of|support everyone|everyone needs me\b/i },
  { label: "invisibility", pattern: /\binvisible|replaceable|unseen|unnoticed|not matter|don'?t matter|nobody sees|no one sees\b/i },
  { label: "support needs", pattern: /\breal help|need help|want help|support need|need support|not in danger\b/i },
];

const TOPIC_FALLBACKS = {
  focusPrioritisation: {
    themes: ["focus", "prioritisation", "task tracking"],
    summary: "The conversation focused on improving focus around urgent and important tasks, keeping scattered notes in one book, making one list, and prioritising only a few items for now.",
    nextStep: "Review the new book/list and choose the few tasks that need attention now.",
    nextFollowUpFocus: "Review the new book/list and choose the few tasks that need attention now.",
  },
  highClinicalDissociation: {
    themes: ["DID-related confusion", "memory gaps", "shame", "relationship fear", "fear of abandonment", "hiding"],
    summary: "The conversation focused on DID-related confusion, memory gaps, shame, fear of being seen as unstable, and fear that the member's partner may leave because of switches or hidden internal chaos.",
    nextStep: "Check whether the member has appropriate professional support for the diagnosis and memory gaps, then explore one safe relational support step.",
    nextFollowUpFocus: "Check whether the member has appropriate professional support for the diagnosis and memory gaps, then explore one safe relational support step.",
  },
  identityBoundary: {
    themes: ["identity boundary", "trust", "role clarity"],
    summary: "The member tested Nia's identity boundary. Nia clarified that she cannot impersonate another person or claim credentials she does not hold, while continuing to offer support as Nia.",
    nextStep: "Continue the conversation directly with Nia, without roleplay or impersonation.",
  },
  relationshipAttachment: {
    themes: ["relationships", "attachment", "letting go"],
    summary: "The conversation focused on relationship attachment, reciprocity, and what it may mean to let go of a connection that is not mutual.",
    nextStep: "Explore what healthy mutual connection looks like for you now.",
  },
  burnoutTunnelVision: {
    themes: ["burnout", "focus", "recovery"],
    summary: "The member described burnout and tunnel vision, with drive, rest, and fear of falling behind showing up clearly.",
    nextStep: "Review how your planned breaks affected your focus and recovery.",
  },
  workloadStructure: {
    themes: ["workload structure", "clarity", "focus"],
    summary: "The conversation focused on the workload structure the member explicitly described, including what needed to be made clearer or more manageable.",
    nextStep: "Choose one grounded workload-management step supported by this session.",
    nextFollowUpFocus: "Review what changed after the workload-management step supported by this session.",
  },
  familyPressure: {
    themes: ["family pressure"],
    summary: "The conversation focused on explicit family pressure and the expectations the member named in the session.",
    nextStep: "Explore the family expectation that felt most present in the conversation.",
  },
  avoidance: {
    themes: ["avoidance", "procrastination", "action block"],
    summary: "The member recognised an avoidance pattern, but awareness alone was not enough to shift action. The stronger coaching focus was what acting might mean, expose, cost, or protect against before moving to environment or action changes.",
    nextStep: "Notice what the task seems to protect you from before choosing the next move.",
  },
};

function normalizeText(value = "") {
  return String(value || "").replace(/[’‘]/g, "'").replace(/\s+/g, " ").trim();
}

export function isInternalFailClosedSummaryText(value = "") {
  return INTERNAL_FAIL_CLOSED_TEXT_PATTERN.test(normalizeText(value));
}

function normalizeSummaryUnderstandingText(value = "") {
  const typoMap = {
    scheduole: "schedule",
    tommorow: "tomorrow",
    coah: "coach",
  };
  return normalizeText(value)
    .toLowerCase()
    .replace(/\bdon't\b/g, "do not")
    .replace(/\bcan't\b/g, "cannot")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean)
    .map((token) => typoMap[token] || token)
    .join(" ");
}

const STALE_THEME_GUARDS = [
  { label: "burnout", pattern: /\b(burnout|burnt out|burned out|tunnel vision)\b/i },
  { label: "book tracking", pattern: /\b(book\/list|one book|new book|book|scattered notes|notes in one book)\b/i },
  { label: "marketing", pattern: /\bmarketing\b/i },
  { label: "workload", pattern: /\b(workload|weekly boundary|work structure|too many moving pieces)\b/i },
  { label: "family", pattern: /\b(family|parents?|mother|father)\b/i },
  { label: "partnership", pattern: /\b(partnership|partner|reciprocity)\b/i },
  { label: "legacy", pattern: /\blegacy\b/i },
  { label: "self-trust", pattern: /\bself[-\s]?trust\b/i },
  { label: "work rhythm", pattern: /\bwork rhythm\b/i },
  { label: "boundaries", pattern: /\bboundar(?:y|ies)\b/i },
];

function hasUnsupportedGeneratedTheme(generatedText = "", messages = []) {
  const generated = normalizeText(generatedText);
  if (!generated) return false;
  const current = conversationText(messages);
  return STALE_THEME_GUARDS.some((entry) => entry.pattern.test(generated) && !entry.pattern.test(current));
}

function userConversationText(messages = []) {
  return messageText(messages, "user").join(" ").toLowerCase();
}

function buildLowContextSummary(options = {}) {
  return {
    summaryText: LOW_CONTEXT_SUMMARY_TEXT,
    detectedThemes: ["current session"],
    suggestedNextStep: LOW_CONTEXT_NEXT_STEP,
    nextFollowUpFocus: LOW_CONTEXT_NEXT_STEP,
    flagSuggested: false,
    generatedAt: options.generatedAt || Date.now(),
    source: options.source || "low-context-placeholder",
    fallbackUsed: true,
  };
}

function preserveSummaryAuditFields(normalizedSummary, sourceSummary) {
  if (!normalizedSummary || typeof normalizedSummary !== "object") return normalizedSummary;
  if (!sourceSummary || typeof sourceSummary !== "object") return normalizedSummary;
  return SUMMARY_AUDIT_FIELDS.reduce((result, field) => (
    sourceSummary[field] === undefined
      ? result
      : { ...result, [field]: sourceSummary[field] }
  ), normalizedSummary);
}

function buildOperationalSupportSummary(messages = [], coachingState = {}, options = {}) {
  const intent = classifyOperationalIntent(messages);
  const themes = [
    intent.inaccurateRecap ? "record accuracy" : "",
    intent.missingSummary || intent.deliveryComplaint ? "missing summary" : "",
    intent.continuityRequest ? "session continuity" : "",
    intent.reminderComplaint ? "reminder accuracy" : "",
    intent.escalationRequest ? "operational escalation" : "",
    intent.recordRequest ? "conversation records" : "",
  ].filter(Boolean);
  let summaryText = "The member raised an operational records or continuity issue that requires factual support handling.";
  if (intent.inaccurateRecap) {
    summaryText = "The member rejected an inaccurate recap and asked for an accurate record of the conversation.";
  } else if (intent.escalationRequest && intent.establishedContext) {
    summaryText = "The member requested escalation of an established records, summary, or continuity complaint.";
  } else if (intent.escalationRequest) {
    summaryText = "The member requested an operational escalation, but the specific complaint was not established in the available session context.";
  } else if (intent.reminderComplaint) {
    summaryText = "The member reported that the reminder details were wrong.";
  } else if (intent.missingSummary || intent.deliveryComplaint) {
    summaryText = "The member reported that expected notes or a summary were missing or had not been received.";
  } else if (intent.recordRequest || intent.continuityRequest) {
    summaryText = "The member requested access to an earlier conversation record, summary, or saved continuity context.";
  }
  if (intent.hasCoachingTopic && intent.residualText) {
    summaryText = `${summaryText} The same message also asked to continue with: ${normalizeText(intent.residualText)}`;
  }
  const suggestedNextStep = "Review the records or continuity issue using identity-matched saved evidence and confirm the outcome without assuming that delivery occurred.";
  return {
    summaryText,
    detectedThemes: themes.length ? themes : ["operational support"],
    suggestedNextStep,
    nextFollowUpFocus: "",
    flagSuggested: false,
    currentStep: coachingState.currentStep || {
      id: "present",
      index: 1,
      confidence: "high",
      reason: "Operational support should be handled before coaching-step inference.",
    },
    detectedBlocks: [],
    targetSkills: [],
    recommendedGames: [],
    gameRationales: [],
    coachingState: {
      ...coachingState,
      responseRoute: "support",
      primaryBlock: "",
      limitingBeliefs: [],
      distortionPatterns: [],
      internalObstacles: [],
      targetSkills: [],
      recommendedGames: [],
      gameRationales: [],
      nextFollowUpFocus: "",
      operationalIntent: intent,
    },
    operationalIntent: intent,
    responseRoute: "support",
    generatedAt: options.generatedAt || Date.now(),
    source: "operational-support-rule",
    fallbackUsed: true,
  };
}

function buildRetirementPlanningSummary(messages = [], coachingState = {}, options = {}) {
  const text = userConversationText(messages);
  const details = [
    /\bown means\b/i.test(text) ? "own means" : "",
    /\bpension(?: income)?\b/i.test(text) ? "pension income" : "",
    /\bliving annuit(?:y|ies)\b/i.test(text) ? "a Living Annuity" : "",
    /\bwithdrawal rate\b/i.test(text) ? "the withdrawal rate" : "",
    /\binflation\b/i.test(text) ? "inflation" : "",
    /\bmonthly income\b/i.test(text) ? "monthly income needs" : "",
    /\btax\b/i.test(text) ? "tax implications" : "",
    /\bpersonal funds?\b/i.test(text) ? "personal funds" : "",
  ].filter(Boolean);
  const themes = [
    "retirement planning",
    /\bpension\b/i.test(text) ? "pension income" : "",
    /\bliving annuit(?:y|ies)\b/i.test(text) ? "Living Annuity" : "",
    /\binflation\b/i.test(text) ? "inflation" : "",
    /\btax\b/i.test(text) ? "tax implications" : "",
  ].filter(Boolean);
  const summaryText = `The conversation was a factual retirement-income planning discussion covering ${details.join(", ")}.`;
  const suggestedNextStep = /\btax\b/i.test(text)
    ? "Compare the available retirement-income options using the figures, tax implications, and professional financial guidance relevant to the member's circumstances."
    : "Compare the available retirement-income options using the relevant figures and professional financial guidance for the member's circumstances.";
  return {
    summaryText,
    detectedThemes: themes,
    suggestedNextStep,
    nextFollowUpFocus: "",
    flagSuggested: false,
    currentStep: coachingState.currentStep || {
      id: "present",
      index: 1,
      confidence: "high",
      reason: "The session is factual planning rather than blocking-language evidence.",
    },
    detectedBlocks: [],
    targetSkills: [],
    recommendedGames: [],
    gameRationales: [],
    coachingState: {
      ...coachingState,
      responseRoute: "inform",
      primaryBlock: "",
      limitingBeliefs: [],
      distortionPatterns: [],
      internalObstacles: [],
      targetSkills: [],
      recommendedGames: [],
      gameRationales: [],
      nextFollowUpFocus: "",
    },
    factualPlanning: classifyFactualPlanningIntent(messages),
    responseRoute: "inform",
    generatedAt: options.generatedAt || Date.now(),
    source: "factual-retirement-planning-rule",
    fallbackUsed: true,
  };
}

function isLowContextNonSafetySession(messages = []) {
  if (!Array.isArray(messages) || !messages.length) return true;
  if (hasSafetySessionSignal(messages) || hasCumulativeSafetyAdjacentSessionSignal(messages)) return false;
  const userLines = messageText(messages, "user");
  return userLines.length <= 1 || messages.length <= 3;
}

function hasSafetySessionSignal(messages = []) {
  return hasActivePersonalSafetySignal(messages);
}

function hasAmbiguousSafetySignal(messages = []) {
  const haystack = conversationText(messages).replace(/[’‘]/g, "'");
  return /\b(i don'?t know if i can keep going|i do not know if i can keep going|i can'?t keep going|i cannot keep going|can'?t keep going|cannot keep going|i can'?t do this anymore|i cannot do this anymore|i'?m done|i am done|i'?m at breaking point|i am at breaking point)\b/i.test(haystack);
}

function hasPracticalSafetyClarification(messages = []) {
  const haystack = messageText(messages, "user").join(" ").toLowerCase().replace(/[’‘]/g, "'");
  return hasPracticalContextClarification(haystack) && !hasSafetySessionSignal(messages);
}

function hasResolvedAmbiguousSafetyClarification(messages = []) {
  return hasAmbiguousSafetySignal(messages) && hasPracticalSafetyClarification(messages) && !hasSafetySessionSignal(messages);
}

function hasCumulativeSafetyAdjacentSessionSignal(messages = []) {
  const contextualState = getSafetyContextState(messages);
  if (contextualState.activePersonalSafety || hasExplicitSafetyDeescalation(messages)) return false;
  if (contextualState.latestClassification.subject === "third_person") return false;
  const currentSafetyWindow = getSafetyContextWindow(messages, contextualState);
  const userLines = messageText(currentSafetyWindow, "user");
  const latest = userLines[userLines.length - 1] || "";
  return Boolean(detectCumulativeSafetyAdjacentSignal(currentSafetyWindow, latest));
}

function buildPracticalClarificationSummary() {
  return {
    summaryText: "The member initially used ambiguous language that Nia treated cautiously, then clarified they meant difficulty continuing with a practical task. Nia shifted back into practical coaching support.",
    detectedThemes: ["practical clarification", "task support"],
    suggestedNextStep: "Continue with the practical task support and confirm the smallest next step.",
    nextFollowUpFocus: "Continue with the practical task support and confirm the smallest next step.",
    flagSuggested: false,
  };
}

function hasThirdPersonSafetyReference(messages = []) {
  const state = getSafetyContextState(messages);
  return Boolean(
    state.latestClassification.subject === "third_person" ||
    (
      state.latestClassification.classification === "explicit_deescalation" &&
      state.hasThirdPersonReference &&
      state.lastPersonalTriggerIndex < 0
    )
  );
}

function hasFirstPersonHistoricalSafetyReference(messages = []) {
  const state = getSafetyContextState(messages);
  return state.latestClassification.classification === "first_person_historical" && !state.activePersonalSafety;
}

function buildThirdPersonSafetyContextSummary(messages = []) {
  const state = getSafetyContextState(messages);
  const haystack = userConversationText(messages);
  const friendContext = /\bfriends?\b/i.test(haystack);
  const familyContext = /\b(family|mother|father|parent|sibling|brother|sister|relative)\b/i.test(haystack);
  const boundaryContext = /\bboundar(?:y|ies)|what (?:i|we) can do|how (?:i|we) can support|support(?:ing)?\b/i.test(haystack);
  const themes = [
    friendContext ? "supporting friends" : "supporting another person",
    familyContext ? "family relationships" : "",
    boundaryContext || friendContext ? "boundaries" : "support options",
  ].filter(Boolean);
  const currentConcern = state.otherPersonCurrentConcern;
  return {
    summaryText: currentConcern
      ? "The member discussed a current safety concern affecting another person. The conversation focused on how to connect that person with immediate human support without treating the member as personally unsafe."
      : `The conversation focused on ${friendContext ? "supporting friends" : "supporting another person"}${familyContext ? " through difficult family circumstances" : ""}, including how to offer care without taking responsibility for another person's history or choices.`,
    detectedThemes: themes,
    suggestedNextStep: currentConcern
      ? "Clarify how immediate the other person's risk may be and help connect them with trusted, local human support."
      : `Clarify what support you can offer ${friendContext ? "your friends" : "the other person"} while holding a boundary around what is not yours to carry.`,
    nextFollowUpFocus: currentConcern
      ? "Clarify how immediate the other person's risk may be and help connect them with trusted, local human support."
      : `Clarify what support you can offer ${friendContext ? "your friends" : "the other person"} while holding a boundary around what is not yours to carry.`,
    flagSuggested: false,
  };
}

function buildHistoricalPersonalSafetySummary() {
  return {
    summaryText: "The member referred to a personal safety experience from the past without describing a current intention or immediate danger. Nia checked whether the concern was entirely historical before continuing.",
    detectedThemes: ["historical safety context", "current-state clarification"],
    suggestedNextStep: "Confirm whether the experience is entirely historical, then continue with the member's present coaching concern.",
    nextFollowUpFocus: "Confirm whether the experience is entirely historical, then continue with the member's present coaching concern.",
    flagSuggested: false,
  };
}

function buildExplicitSafetyDeescalationSummary() {
  return {
    summaryText: "The member clarified that there was no personal safety concern, and Nia returned to the member's actual coaching topic.",
    detectedThemes: ["context clarification", "coaching support"],
    suggestedNextStep: "Continue from the member's latest stated coaching concern.",
    nextFollowUpFocus: "Continue from the member's latest stated coaching concern.",
    flagSuggested: false,
  };
}

function hasHumanCoachReviewRequest(messages = []) {
  const haystack = normalizeSummaryUnderstandingText(messageText(messages, "user").join(" "));
  if (!haystack) return false;
  return /\b(speak|talk|chat)\b.{0,30}\b(real|human|actual)\b.{0,30}\b(coach|person|someone)\b/i.test(haystack)
    || /\b(real|human|actual)\b.{0,30}\b(coach|person)\b/i.test(haystack)
    || /\b(can i get|i need|request|please get me)\b.{0,40}\b(1-on-1|one-on-one|human coach|coach support|coach review|(?:a|the|my) coach)\b/i.test(haystack);
}

function hasScheduledFollowUpRequest(messages = []) {
  const haystack = normalizeSummaryUnderstandingText(messageText(messages, "user").join(" "));
  return /\b(schedule|book|arrange|set up|plan|move|reschedule)\b.{0,60}\b(follow up|followup|session|check in|reminder)\b/i.test(haystack);
}

function joinSummaryParts(parts = []) {
  const cleanParts = parts.map((part) => normalizeText(part)).filter(Boolean);
  if (cleanParts.length <= 2) return cleanParts.join(" and ");
  return `${cleanParts.slice(0, -1).join(", ")}, and ${cleanParts[cleanParts.length - 1]}`;
}

function buildHumanCoachReviewRequestSummary(messages = []) {
  const practicalMeeting = /\bmeeting|call|workshop|class\b/i.test(normalizeSummaryUnderstandingText(messageText(messages, "user").join(" ")));
  const scheduled = hasScheduledFollowUpRequest(messages);
  const parts = [
    practicalMeeting
      ? "clarified that the difficulty related to a meeting"
      : "described a non-safety coaching concern",
    scheduled ? "scheduled a follow-up" : "",
    "requested human coach review",
  ].filter(Boolean);
  return {
    summaryText: `The member ${joinSummaryParts(parts)}.`,
    detectedThemes: [
      practicalMeeting ? "meeting discomfort" : "coaching support",
      scheduled ? "scheduled follow-up" : "",
      "human coach review",
    ].filter(Boolean),
    suggestedNextStep: "Review the human coach request and continue through the appropriate coach-review follow-up path.",
    nextFollowUpFocus: "Review the human coach request and continue through the appropriate coach-review follow-up path.",
    flagSuggested: false,
  };
}

function buildSafetySessionSummary(flagSuggested = true) {
  return {
    summaryText: "The member shared a safety concern or self-harm-adjacent signal. Nia paused ordinary coaching, checked immediate safety, and directed the member toward immediate human support rather than standard coaching actions.",
    detectedThemes: ["safety concern", "human support", "coach review"],
    suggestedNextStep: flagSuggested
      ? "Confirm immediate safety and ensure human support or coach review follows up."
      : "Confirm immediate safety and contact appropriate human support.",
    flagSuggested: Boolean(flagSuggested),
  };
}

function buildCumulativeSafetyAdjacentSummary(messages = []) {
  const haystack = conversationText(messages);
  const identityText = hasIdentityBoundarySessionSignal(haystack)
    ? "The member tested Nia's identity boundary, which Nia preserved. "
    : "";
  const focusParts = [
    /\btrauma|triggered|flashback|past\b/i.test(haystack) ? "trauma-related distress" : "",
    /\bchronic pain|pain|body hurts|physical pain\b/i.test(haystack) ? "chronic pain" : "",
    /\blonely|loneliness|alone|no one\b/i.test(haystack) ? "loneliness" : "",
    /\banxiety|depression|depressed|panic|fear\b/i.test(haystack) ? "anxiety, depression, or fear" : "",
    /\bbreaking down|break down|collapse|fall apart\b/i.test(haystack) ? "fear of breaking down" : "",
  ].filter(Boolean);
  const focusText = focusParts.length
    ? focusParts.join(", ")
    : "distress, fear of collapse, and repeated safety-adjacent language";
  return {
    summaryText: `${identityText}The session then focused on ${focusText}, and safety-adjacent feelings of being trapped or close to going under.`,
    detectedThemes: mergeThemeLists(
      ["safety-adjacent distress", "human support", "coach review"],
      [
        hasIdentityBoundarySessionSignal(haystack) ? "identity boundary" : "",
        ...focusParts,
      ]
    ),
    suggestedNextStep: "Confirm immediate safety and ensure human support or coach review follows up.",
    nextFollowUpFocus: "Confirm immediate safety and ensure human support or coach review follows up.",
    flagSuggested: true,
  };
}

function normalizeSummaryAudience(audience = "") {
  const normalized = String(audience || "").trim().toLowerCase();
  if (normalized === "self" || normalized === "user") return "self";
  if (normalized === "system" || normalized === "report") return normalized;
  return "reviewer";
}

function splitSummarySentences(text = "") {
  return normalizeText(text)
    .split(/(?<=[.!?])\s+/)
    .map((part) => normalizeText(part))
    .filter(Boolean);
}

function joinSummarySentences(parts = []) {
  return parts.map((part) => normalizeText(part)).filter(Boolean).join(" ").trim();
}

function rewriteSentence(text = "", rules = []) {
  for (const [pattern, replacement] of rules) {
    if (pattern.test(text)) return text.replace(pattern, replacement);
  }
  return text;
}

function rewriteAll(text = "", rules = []) {
  return rules.reduce((result, [pattern, replacement]) => result.replace(pattern, replacement), text);
}

function convertRawFirstPersonToCoachSentence(text = "") {
  const normalized = normalizeText(text);
  if (!normalized) return "";
  const converted = rewriteSentence(normalized, [
    [/^I am\b/i, "they are"],
    [/^I'm\b/i, "they are"],
    [/^I was\b/i, "they were"],
    [/^I feel\b/i, "they feel"],
    [/^I felt\b/i, "they felt"],
    [/^I need\b/i, "they need"],
    [/^I want\b/i, "they want"],
    [/^I have\b/i, "they have"],
    [/^I had\b/i, "they had"],
    [/^I can\b/i, "they can"],
    [/^I can't\b/i, "they can't"],
    [/^I cannot\b/i, "they cannot"],
    [/^I will\b/i, "they will"],
    [/^I would\b/i, "they would"],
    [/^I'd\b/i, "they would"],
    [/^My\b/i, "their"],
    [/^I\b/i, "they"],
  ]);
  return `The client said ${converted}`;
}

function convertCoachSentenceToUser(text = "") {
  const normalized = normalizeText(text);
  if (!normalized) return "";
  const converted = rewriteSentence(normalized, [
    [/^The client said they are\b/i, "You said you're"],
    [/^The client said they were\b/i, "You said you were"],
    [/^The client said they feel\b/i, "You said you feel"],
    [/^The client said they felt\b/i, "You said you felt"],
    [/^The client said they need\b/i, "You said you need"],
    [/^The client said they want\b/i, "You said you want"],
    [/^The client said they have\b/i, "You said you have"],
    [/^The client said they had\b/i, "You said you had"],
    [/^The client said they can\b/i, "You said you can"],
    [/^The client said they can't\b/i, "You said you can't"],
    [/^The client said they cannot\b/i, "You said you cannot"],
    [/^The client said they will\b/i, "You said you will"],
    [/^The client said they would\b/i, "You said you would"],
    [/^The client said feeling\b/i, "You described feeling"],
    [/^The client said(?: that)?\b/i, "You said"],
    [/^The client expressed(?: that)?\b/i, "You said"],
    [/^The client mentioned(?: that)?\b/i, "You described"],
    [/^The client shared(?: that)?\b/i, "You shared"],
    [/^The client reported feeling\b/i, "You described feeling"],
    [/^The client reported(?: that)?\b/i, "You noticed"],
    [/^The client described(?: that)?\b/i, "You described"],
    [/^The client noted(?: that)?\b/i, "You noted"],
    [/^The client was dealing with\b/i, "You were dealing with"],
    [/^The client is dealing with\b/i, "You were dealing with"],
    [/^The client was struggling with\b/i, "You were struggling with"],
    [/^The client is struggling with\b/i, "You were struggling with"],
    [/^The client felt\b/i, "You felt"],
    [/^The client feels\b/i, "You feel"],
    [/^The member said they are\b/i, "You said you're"],
    [/^The member said they were\b/i, "You said you were"],
    [/^The member said they feel\b/i, "You said you feel"],
    [/^The member said they felt\b/i, "You said you felt"],
    [/^The member said feeling\b/i, "You described feeling"],
    [/^The member said(?: that)?\b/i, "You said"],
    [/^The member mentioned(?: that)?\b/i, "You described"],
    [/^The member shared(?: that)?\b/i, "You shared"],
    [/^The member reported feeling\b/i, "You described feeling"],
    [/^The member reported(?: that)?\b/i, "You noticed"],
    [/^The member described(?: that)?\b/i, "You described"],
    [/^The member was dealing with\b/i, "You were dealing with"],
    [/^The member is dealing with\b/i, "You were dealing with"],
    [/^The member was struggling with\b/i, "You were struggling with"],
    [/^The member is struggling with\b/i, "You were struggling with"],
    [/^The member felt\b/i, "You felt"],
    [/^The member feels\b/i, "You feel"],
    [/^Client mentioned(?: that)?\b/i, "You described"],
    [/^Client reported feeling\b/i, "You described feeling"],
    [/^Client reported(?: that)?\b/i, "You noticed"],
    [/^Client could\b/i, "You could"],
    [/^They reported feeling\b/i, "You described feeling"],
    [/^They reported(?: that)?\b/i, "You noticed"],
    [/^They described(?: that)?\b/i, "You described"],
    [/^They shared(?: that)?\b/i, "You shared"],
    [/^They said(?: that)?\b/i, "You said"],
    [/^They are dealing with\b/i, "You were dealing with"],
    [/^They were dealing with\b/i, "You were dealing with"],
    [/^This session indicates\b/i, "You described"],
    [/^This session shows\b/i, "You described"],
  ]);
  return rewriteAll(converted, [
    [/\bThe client'?s\b/gi, "your"],
    [/\bthe client'?s\b/gi, "your"],
    [/\bThe member'?s\b/gi, "your"],
    [/\bthe member'?s\b/gi, "your"],
    [/\bThe client has\b/gi, "you have"],
    [/\bthe client has\b/gi, "you have"],
    [/\bThe member has\b/gi, "you have"],
    [/\bthe member has\b/gi, "you have"],
    [/\bThe client\b/gi, "you"],
    [/\bthe client\b/gi, "you"],
    [/\bThe member\b/gi, "you"],
    [/\bthe member\b/gi, "you"],
    [/\bClient\b/g, "You"],
    [/\bclient\b/g, "you"],
    [/\bthey are\b/gi, "you are"],
    [/\bthey were\b/gi, "you were"],
    [/\bthey feel\b/gi, "you feel"],
    [/\bthey felt\b/gi, "you felt"],
    [/\bthey need\b/gi, "you need"],
    [/\bthey want\b/gi, "you want"],
    [/\bthey have\b/gi, "you have"],
    [/\bthey had\b/gi, "you had"],
    [/\bthey can\b/gi, "you can"],
    [/\bthey can't\b/gi, "you can't"],
    [/\bthey cannot\b/gi, "you cannot"],
    [/\bthey will\b/gi, "you will"],
    [/\bthey would\b/gi, "you would"],
    [/\bthey\b/gi, "you"],
    [/\bthem\b/gi, "you"],
    [/\bthey were dealing with\b/gi, "you were dealing with"],
    [/\bthey are dealing with\b/gi, "you were dealing with"],
    [/\bthey were struggling with\b/gi, "you were struggling with"],
    [/\bthey are struggling with\b/gi, "you were struggling with"],
    [/\btheir\b/gi, "your"],
  ]);
}

function formatReviewerSupportText(text = "") {
  const normalized = normalizeText(text);
  if (!normalized) return "";
  const converted = convertUserSentenceToCoach(normalized);
  const reviewerText = rewriteAll(converted, [
    // Reviewer wording is relationship-based: another person's session should not say "you".
    [/\bThe client\b/g, "The member"],
    [/\bthe client\b/g, "the member"],
    [/\bClient\b/g, "The member"],
    [/\bclient\b/g, "member"],
    [/\bThe member reported feeling\b/g, "The member described feeling"],
    [/\bthe member reported feeling\b/g, "the member described feeling"],
    [/\bYou mentioned feeling\b/g, "The member described feeling"],
    [/\byou mentioned feeling\b/g, "the member described feeling"],
    [/\bYou mentioned\b/g, "The member described"],
    [/\byou mentioned\b/g, "the member described"],
    [/\bYou said feeling\b/g, "The member described feeling"],
    [/\byou said feeling\b/g, "the member described feeling"],
    [/\bYou said\b/g, "The member said"],
    [/\byou said\b/g, "the member said"],
    [/\bYou described\b/g, "The member described"],
    [/\byou described\b/g, "the member described"],
    [/\bYou shared\b/g, "The member shared"],
    [/\byou shared\b/g, "the member shared"],
    [/\bYou were\b/g, "The member was"],
    [/\byou were\b/g, "the member was"],
    [/\bYou are\b/g, "The member is"],
    [/\byou are\b/g, "the member is"],
    [/\bYou could\b/g, "The member could"],
    [/\byou could\b/g, "the member could"],
    [/\bYou\b/g, "The member"],
    [/\byou\b/g, "the member"],
    [/\byour\b/gi, "their"],
    [/\btheir task\b/gi, "the task"],
    [/\btheir day\b/gi, "the day"],
    [/\btheir work\b/gi, "the work"],
  ]);
  return cleanUserFacingGrammar(sentenceCaseUser(reviewerText));
}

// Audience is the viewing relationship, not the signed-in role.
// Example self: "The client mentioned feeling tired and they were avoiding starting."
// -> "You described feeling tired and you were avoiding starting."
// Example reviewer: "You described feeling tired and your task felt hard to start."
// -> "The member described feeling tired and the task felt hard to start."

function cleanUserFacingSentence(text = "") {
  const normalized = normalizeText(text);
  if (!normalized) return "";
  const converted = convertCoachSentenceToUser(normalized);
  const cleaned = rewriteAll(converted, [
    [/\bbut is\b/gi, "but you're"],
    [/\band is\b/gi, "and you're"],
    [/\bwhile is\b/gi, "while you're"],
    [/\bbut was\b/gi, "but you were"],
    [/\band was\b/gi, "and you were"],
    [/\bwhile was\b/gi, "while you were"],
  ]);
  return sentenceCaseUser(cleaned);
}

function sentenceCaseUser(text = "") {
  const normalized = normalizeText(text);
  if (!normalized) return "";
  if (/^you\b/.test(normalized)) {
    return `You${normalized.slice(3)}`;
  }
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function cleanUserFacingGrammar(text = "") {
  return rewriteAll(text, [
    [/\bYou'?s\b/g, "Your"],
    [/\byou'?s\b/g, "your"],
    [/\bYou has\b/g, "You have"],
    [/\byou has\b/g, "you have"],
    [/\bTheir has\b/g, "They have"],
    [/\btheir has\b/g, "they have"],
    [/\bYou is\b/g, "You are"],
    [/\byou is\b/g, "you are"],
    [/\bYou was\b/g, "You were"],
    [/\byou was\b/g, "you were"],
    [/\bYou feels\b/g, "You feel"],
    [/\byou feels\b/g, "you feel"],
    [/\bYou tries\b/g, "You try"],
    [/\byou tries\b/g, "you try"],
    [/\bYou said feeling\b/g, "You described feeling"],
    [/\byou said feeling\b/g, "you described feeling"],
    [/\btry try\b/gi, "try"],
    [/\bfocus on focus on\b/gi, "focus on"],
    [/\bname name\b/gi, "name"],
    [/\bTry choose\b/g, "Try choosing"],
    [/\btry choose\b/g, "try choosing"],
    [/\bpreventing them from starting your (?:task|day|work)\b/gi, "preventing you from starting"],
    [/\bpreventing you from starting your (?:task|day|work)\b/gi, "preventing you from starting"],
    [/\bavoidance behavior\b/gi, "avoidance behaviour"],
    [/^Starting with\s+/i, "Start with "],
    [/\bhow to effectively this staff member\b/gi, "how to support this staff member effectively"],
    [/\bhow to a staff member\b/gi, "how to support a staff member"],
    [/\bYou expresses\b/g, "You expressed"],
    [/\byou expresses\b/g, "you expressed"],
    [/\bYou aims\b/g, "You aim"],
    [/\byou aims\b/g, "you aim"],
    [/\bYou needs\b/g, "You need"],
    [/\byou needs\b/g, "you need"],
    [/\bYou wants\b/g, "You want"],
    [/\byou wants\b/g, "you want"],
    [/\bYou should\b/g, "Try"],
    [/\byou should\b/g, "try"],
  ]);
}

function cleanSupportGrammar(text = "") {
  const normalized = normalizeText(text);
  if (!normalized) return "";
  return rewriteAll(normalized, [
    [/\bYou is\b/g, "You are"],
    [/\byou is\b/g, "you are"],
    [/\bYou was\b/g, "You were"],
    [/\byou was\b/g, "you were"],
    [/\bYou feels\b/g, "You feel"],
    [/\byou feels\b/g, "you feel"],
    [/\bNext time you feels\b/g, "Next time you feel"],
    [/\bnext time you feels\b/g, "next time you feel"],
    [/\btry try\b/gi, "try"],
    [/\btry changing changing\b/gi, "try changing"],
    [/\bfocus on focus on\b/gi, "focus on"],
    [/\bname name\b/gi, "name"],
    [/\btry to try to\b/gi, "try to"],
    [/\bTry try\b/g, "Try"],
  ]);
}

function simplifyMalformedUserFacingText(text = "") {
  const normalized = normalizeText(text);
  if (!normalized) return "";
  const suggestedAction = normalized.match(/(?:should|to)\s+([^.!?]+)/i)?.[1];
  if (suggestedAction) {
    return sentenceCaseUser(`Try ${normalizeText(suggestedAction).replace(/^(to)\s+/i, "")}.`);
  }
  const mentioned = normalized.match(/(?:mentioned|reported feeling|said|shared|described)\s+([^.!?]+)/i)?.[1];
  if (mentioned) {
    return sentenceCaseUser(`You described ${normalizeText(mentioned)}.`);
  }
  return sentenceCaseUser("You described what felt most important in the session.");
}

function formatUserFacingSupportText(text = "") {
  const normalized = normalizeText(text);
  if (!normalized) return "";
  const converted = cleanUserFacingGrammar(cleanUserFacingSentence(normalized));
  if (
    /\b(the client|the coach|client|coach)\b/i.test(converted) ||
    /\bYou is\b|\bYou expresses\b|\bYou aims\b/i.test(converted)
  ) {
    const stripped = rewriteAll(converted, [
      [/^The coach suggested(?: that)?\s+/i, ""],
      [/^The coach recommended(?: that)?\s+/i, ""],
      [/^The coach noted(?: that)?\s+/i, ""],
      [/^The client should\b/i, "Try"],
      [/^The client needs to\b/i, "Try"],
      [/^The client aims to\b/i, "Try"],
      [/\bThe coach\b/gi, ""],
      [/\bcoach\b/gi, ""],
      [/\bThe client\b/gi, "you"],
      [/\bclient\b/gi, "you"],
    ]);
    const cleaned = cleanUserFacingGrammar(sentenceCaseUser(normalizeText(stripped)));
    if (
      !/\b(the client|the coach|client|coach)\b/i.test(cleaned) &&
      !/\bYou is\b|\bYou expresses\b|\bYou aims\b/i.test(cleaned)
    ) {
      return cleaned;
    }
    return simplifyMalformedUserFacingText(normalized);
  }
  return converted;
}

export function formatSummarySnippetForAudience(text = "", audience = "coach", maxLength = 120) {
  const source = normalizeText(text);
  if (isLikelyLegacyTruncatedSummary(source)) return "";
  const formatted = formatSupportTextForAudience(source, audience);
  const safeMaxLength = Number.isFinite(Number(maxLength))
    ? Math.max(0, Math.floor(Number(maxLength)))
    : 120;
  if (formatted.length <= safeMaxLength) return formatted;
  if (safeMaxLength === 0) return "";
  if (safeMaxLength === 1) return "…";

  const contentLimit = safeMaxLength - 1;
  const sentences = splitSummarySentences(formatted);
  let snippet = "";
  for (const sentence of sentences) {
    const candidate = normalizeText(`${snippet} ${sentence}`);
    if (candidate.length > contentLimit) break;
    snippet = candidate;
  }
  if (snippet) return `${snippet}…`;

  let wordSafeSnippet = formatted.slice(0, contentLimit).trim();
  if (wordSafeSnippet && !/[\s.,;:!?]/.test(formatted.charAt(wordSafeSnippet.length))) {
    wordSafeSnippet = wordSafeSnippet.replace(/\s+\S*$/, "").trim();
  }
  return wordSafeSnippet ? `${wordSafeSnippet}…` : "…";
}

function convertUserSentenceToCoach(text = "") {
  const normalized = normalizeText(text);
  if (!normalized) return "";
  if (/^The client\b/i.test(normalized)) return normalized;
  if (/^I\b|^I'm\b|^I'\b|^My\b/i.test(normalized)) {
    return convertRawFirstPersonToCoachSentence(normalized);
  }
  return rewriteSentence(normalized, [
    [/^You said you're\b/i, "The client said they are"],
    [/^You said you were\b/i, "The client said they were"],
    [/^You said you feel\b/i, "The client said they feel"],
    [/^You said you felt\b/i, "The client said they felt"],
    [/^You said you need\b/i, "The client said they need"],
    [/^You said you want\b/i, "The client said they want"],
    [/^You said you have\b/i, "The client said they have"],
    [/^You said you had\b/i, "The client said they had"],
    [/^You said you can\b/i, "The client said they can"],
    [/^You said you can't\b/i, "The client said they can't"],
    [/^You said you cannot\b/i, "The client said they cannot"],
    [/^You said you will\b/i, "The client said they will"],
    [/^You said you would\b/i, "The client said they would"],
    [/^You said\b/i, "The client said"],
    [/^You mentioned feeling\b/i, "The client reported feeling"],
    [/^You mentioned\b/i, "The client reported"],
    [/^You shared\b/i, "The client reported"],
    [/^You described\b/i, "The client reported"],
    [/^You noted\b/i, "The client reported"],
    [/^You were dealing with\b/i, "The client was dealing with"],
    [/^You're dealing with\b/i, "The client is dealing with"],
    [/^You are dealing with\b/i, "The client is dealing with"],
    [/^You were struggling with\b/i, "The client was struggling with"],
    [/^You're struggling with\b/i, "The client is struggling with"],
    [/^You feel\b/i, "The client reported feeling"],
    [/^You felt\b/i, "The client reported feeling"],
  ]);
}

export function formatSummaryTextForAudience(text = "", audience = "coach") {
  const normalized = normalizeText(text);
  if (!normalized) return "";
  if (isLikelyLegacyTruncatedSummary(normalized)) return "";
  const target = normalizeSummaryAudience(audience);
  if (target === "self" && isInternalFailClosedSummaryText(normalized)) {
    return LOW_CONTEXT_SUMMARY_TEXT;
  }
  if (target === "self") {
    return joinSummarySentences(splitSummarySentences(normalized).map((sentence) => formatUserFacingSupportText(sentence)));
  }
  if (target === "system" || target === "report") return normalized;
  return joinSummarySentences(splitSummarySentences(normalized).map((sentence) => formatReviewerSupportText(sentence)));
}

export function formatSupportTextForAudience(text = "", audience = "coach") {
  const normalized = normalizeText(text);
  if (!normalized) return "";
  const target = normalizeSummaryAudience(audience);
  if (target === "self" && isInternalFailClosedSummaryText(normalized)) return "";
  if (target === "self") return formatUserFacingSupportText(normalized);
  if (target === "system" || target === "report") return normalized;
  return formatReviewerSupportText(normalized);
}

function messageText(messages = [], role) {
  return messages
    .filter((m) => !role || m?.role === role)
    .map((m) => normalizeText(m?.content))
    .filter(Boolean);
}

function conversationText(messages = []) {
  return messageText(messages).join(" ").toLowerCase();
}

function hasWebsiteQualityCheckCommitment(messages = []) {
  const memberText = messageText(messages, "user").join(" ").toLowerCase();
  if (!memberText) return false;
  const websiteTask = /\bwebsite\b/i.test(memberText) &&
    /\b(?:quality\s+(?:check|review)|check(?:ing)?\s+(?:the\s+|my\s+)?website)\b/i.test(memberText);
  const committedAction = /\b(?:i(?:'ll| will| plan to| am going to)|my action(?: is)?|agreed action)\b/i.test(memberText);
  return Boolean(
    websiteTask &&
    committedAction &&
    /\bhttps\b/i.test(memberText) &&
    /\bcontact\s+form\b/i.test(memberText) &&
    /\b(?:write|note|record)\b.{0,80}\b(?:problem|issue)\b/i.test(memberText)
  );
}

function buildWebsiteQualityCheckSummary(messages = [], options = {}) {
  if (!hasWebsiteQualityCheckCommitment(messages)) return null;
  const currentStep = options.currentStep || inferCurrentStep(messages);
  return {
    summaryText: `The member chose a website quality check as the first work priority and agreed to ${WEBSITE_QUALITY_CHECK_ACTION.charAt(0).toLowerCase()}${WEBSITE_QUALITY_CHECK_ACTION.slice(1)}`,
    participantObjective: WEBSITE_QUALITY_CHECK_OBJECTIVE,
    agreedActions: [WEBSITE_QUALITY_CHECK_ACTION],
    completedActions: [],
    detectedThemes: ["work prioritisation", "website quality check", "quality assurance"],
    suggestedNextStep: WEBSITE_QUALITY_CHECK_ACTION,
    nextFollowUpFocus: WEBSITE_QUALITY_CHECK_FOLLOW_UP,
    flagSuggested: false,
    currentStep,
    detectedBlocks: [],
    targetSkills: [],
    recommendedGames: [],
    gameRationales: [],
    coachingState: {
      currentStep,
      currentGoal: WEBSITE_QUALITY_CHECK_OBJECTIVE,
      presentingIssue: "Choosing a first work priority.",
      currentStory: "The member chose a website quality check.",
      primaryBlock: "",
      internalObstacles: [],
      externalObstacles: [],
      limitingBeliefs: [],
      distortionPatterns: [],
      targetSkills: [],
      recommendedGames: [],
      gameRationales: [],
      actions: [WEBSITE_QUALITY_CHECK_ACTION],
      completionAction: "",
      immediateTarget: WEBSITE_QUALITY_CHECK_ACTION,
      nextFollowUpFocus: WEBSITE_QUALITY_CHECK_FOLLOW_UP,
    },
    generatedAt: options.generatedAt || Date.now(),
    source: options.source || "grounded-transcript-fallback",
    fallbackUsed: options.fallbackUsed !== false,
  };
}

const STRUCTURED_GROUNDING_STOP_WORDS = new Set([
  "a", "an", "and", "are", "as", "at", "be", "been", "before", "by", "for", "from",
  "had", "has", "have", "i", "in", "is", "it", "member", "my", "nia", "of", "on",
  "one", "or", "session", "that", "the", "their", "them", "they", "this", "to", "was",
  "were", "what", "while", "with", "without", "you", "your",
]);

function groundingStem(value = "") {
  const token = normalizeText(value).toLowerCase();
  const mapped = {
    actions: "action",
    completed: "complete",
    completing: "complete",
    learned: "learn",
    learning: "learn",
    learnt: "learn",
    persisted: "persist",
    persistence: "persist",
    persistent: "persist",
    practiced: "practice",
    practicing: "practice",
    practised: "practice",
    practising: "practice",
    priorities: "priority",
    responses: "response",
    wrote: "write",
    written: "write",
  }[token];
  if (mapped) return mapped;
  if (token.length > 6 && token.endsWith("ing")) return token.slice(0, -3);
  if (token.length > 5 && token.endsWith("ed")) return token.slice(0, -2);
  if (token.length > 5 && token.endsWith("s")) return token.slice(0, -1);
  return token;
}

function groundingTokens(value = "") {
  return normalizeText(value)
    .toLowerCase()
    .replace(/[^a-z0-9']+/g, " ")
    .split(/\s+/)
    .map(groundingStem)
    .filter((token) => token.length > 2 && !STRUCTURED_GROUNDING_STOP_WORDS.has(token));
}

function isGroundedStructuredText(value = "", transcriptText = "") {
  const candidate = Array.from(new Set(groundingTokens(value)));
  if (!candidate.length) return false;
  const transcript = new Set(groundingTokens(transcriptText));
  const matches = candidate.filter((token) => transcript.has(token)).length;
  return matches >= Math.min(2, candidate.length) && matches / candidate.length >= 0.5;
}

function summaryCoversStructuredText(value = "", summaryText = "") {
  const candidate = Array.from(new Set(groundingTokens(value)));
  if (!candidate.length) return false;
  const summary = new Set(groundingTokens(summaryText));
  const matches = candidate.filter((token) => summary.has(token)).length;
  return matches >= Math.min(2, candidate.length) && matches / candidate.length >= 0.75;
}

function cleanStructuredField(value = "") {
  return normalizeText(value).replace(/^[•*-]\s*/, "");
}

function uniqueStructuredList(values = [], maxItems = 5) {
  const seen = new Set();
  return (Array.isArray(values) ? values : [])
    .map((value) => cleanStructuredField(value))
    .filter(Boolean)
    .filter((value) => {
      const key = value.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, maxItems);
}

function extractParticipantObjective(messages = []) {
  const userLines = messageText(messages, "user");
  for (const line of userLines) {
    const infinitive = line.match(/\b(?:i want|i need|i would like|i'd like|my goal is|my objective is)\s+to\s+([^.!?]+)/i);
    if (infinitive?.[1]) return cleanStructuredField(infinitive[1]);
    const nounGoal = line.match(/\b(?:i want|i need|i would like|i'd like)\s+([^.!?]+)/i);
    if (nounGoal?.[1]) return cleanStructuredField(`Have ${nounGoal[1]}`);
    const namedGoal = line.match(/\b(?:my goal|my objective|the objective)\s+(?:is|was)\s+([^.!?]+)/i);
    if (namedGoal?.[1]) return cleanStructuredField(namedGoal[1]);
  }
  return "";
}

function extractAgreedActions(messages = []) {
  const actions = [];
  messageText(messages, "user").forEach((line) => {
    const participantOwnedText = maskQuotedParticipantSpeech(line);
    const matches = participantOwnedText.match(/\b(?:my agreed action is(?: to)?|i will|i'll|i am going to|i'm going to|i plan to|i decided(?: to)?|i have decided to|i've decided to|my plan is(?: to)?|my decision is(?: to)?|my next step is|what i'm going to do is(?: to)?|the realistic task is to)\b[^.!?]*/gi) || [];
    actions.push(...matches);
  });
  return uniqueStructuredList(actions);
}

function latestAgreedActionBefore(messages = [], beforeIndex = 0) {
  const agreedActions = extractAgreedActions(
    (Array.isArray(messages) ? messages : []).slice(0, beforeIndex)
  );
  return agreedActions[agreedActions.length - 1] || "";
}

function isDocumentationAction(value = "") {
  const normalized = cleanStructuredField(value)
    .replace(/^(?:i will|i'll|i can|i am going to|i'm going to|i plan to|i decided(?: to)?|i have decided to|i've decided to|my plan is(?: to)?|my decision is(?: to)?|my next step is|what i'm going to do is(?: to)?|the realistic task is to)\s+/i, "")
    .replace(/[.!?]+$/, "");
  return /^(?:write|note|record)\b[^.!?]*\bdown\b/i.test(normalized);
}

function isDocumentationCompletion(value = "") {
  return /\b(?:wrote|written|noted|recorded)\b[^.!?]*\bdown\b/i.test(
    normalizeText(value)
  );
}

function isSchedulingAction(value = "") {
  const normalized = cleanStructuredField(value)
    .replace(/^(?:i will|i'll|i can|i am going to|i'm going to|i plan to|i decided(?: to)?|i have decided to|i've decided to|my plan is(?: to)?|my decision is(?: to)?|my next step is|what i'm going to do is(?: to)?|the realistic task is to)\s+/i, "")
    .replace(/[.!?]+$/, "");
  return /^(?:book|schedule|arrange|set up)\b/i.test(normalized);
}

function isSchedulingCompletion(value = "") {
  return /\b(?:book(?:ed|ing)?|schedul(?:ed|ing)?)\b/i.test(
    normalizeText(value)
  );
}

function isCompletionInquiryStatement(text = "") {
  return /\b(?:check|ask|see|confirm)\s+(?:whether|if)\s+i\s+(?:have\s+)?(?:completed|finished|done)\b/i.test(
    normalizeText(text)
  );
}

function isReferentialCompletionStatement(text = "") {
  const normalized = normalizeText(text);
  return Boolean(
    /\b(?:it|this|that|them)\b/i.test(normalized) ||
    /^(?:yes[, ]+)?(?:done|completed|finished)[.!]?$/i.test(normalized) ||
    /\b(?:i have|i've)\s+(?:now\s+|already\s+|just\s+)?(?:completed|finished|sent|called|done)\s*[.!?]?$/i.test(normalized) ||
    /\bi\s+(?:completed|finished|sent|called|did)\s*[.!?]?$/i.test(normalized)
  );
}

function extractCompletedActions(messages = []) {
  const completed = [];
  (Array.isArray(messages) ? messages : []).forEach((message, index) => {
    if (String(message?.role || "").toLowerCase() !== "user") return;
    const line = normalizeText(message?.content || message?.text || "");
    if (isCompletionInquiryStatement(line)) return;
    const actionText = maskQuotedParticipantSpeech(line)
      .replace(/\bsession\s+(?:is\s+)?complete\b/gi, "")
      .replace(
        /\b(?:i have|i've|i)\s+(?:completed|finished)\s+(?:this|the)\s+session\b/gi,
        ""
      )
      .trim();
    const matches = [
      ...(
        actionText.match(
          /\b(?:i have|i've)\s+(?:now\s+|already\s+|just\s+)?(?:written|noted|recorded|completed|finished|sent|called|booked|scheduled|done)\b[^.!?]*/gi
        ) || []
      ),
      ...(
        actionText.match(
          /\bi\s+(?:wrote|noted|recorded|completed|finished|sent|called|booked|scheduled|did)\b[^.!?]*/gi
        ) || []
      ),
      ...(
        actionText.match(
          /\bi\s+(?:managed|was able)\s+to\s+(?:write|complete|finish|send|call|book|schedule|do)\b[^.!?]*/gi
        ) || []
      ),
      ...(
        actionText.match(
          /\bi\s+got\s+(?:it|that|the action|the task)\s+done\b[^.!?]*/gi
        ) || []
      ),
      ...(
        actionText.match(
          /\b(?:it|that|the action|the task)\s+(?:is|'s|was)\s+(?:done|complete|completed|finished)\b[^.!?]*/gi
        ) || []
      ),
      ...(
        /^(?:yes[, ]+)?(?:done|completed|finished)[.!]?$/i.test(actionText)
          ? [actionText]
          : []
      ),
    ];
    matches.forEach((match) => {
      const agreedAction = latestAgreedActionBefore(messages, index);
      const documentationCompletion = isDocumentationCompletion(match);
      const schedulingCompletion = isSchedulingCompletion(match);
      if (documentationCompletion && !isDocumentationAction(agreedAction)) {
        return;
      }
      if (schedulingCompletion && !isSchedulingAction(agreedAction)) {
        return;
      }
      const referential = isReferentialCompletionStatement(match);
      if (referential && !agreedAction) {
        return;
      }
      completed.push(
        documentationCompletion || schedulingCompletion || referential
          ? (agreedAction || match)
          : match
      );
    });
  });
  return uniqueStructuredList(completed);
}

function completedActionSemanticKind(value = "") {
  if (isDocumentationAction(value) || isDocumentationCompletion(value)) {
    return "documentation";
  }
  if (isSchedulingAction(value) || isSchedulingCompletion(value)) {
    return "scheduling";
  }
  return "execution";
}

function completedActionMatchesEvidence(value = "", evidence = "") {
  return completedActionSemanticKind(value) ===
    completedActionSemanticKind(evidence) &&
    isGroundedStructuredText(value, evidence);
}

function actionForSummary(value = "") {
  const normalized = cleanStructuredField(value).replace(/[.!?]+$/, "");
  const cleaned = normalizeText(normalized
    .replace(/^(?:my agreed action is(?: to)?|i will|i'll|i can|i am going to|i'm going to|i plan to|i decided(?: to)?|i have decided to|i've decided to|my plan is(?: to)?|my decision is(?: to)?|my next step is|what i'm going to do is(?: to)?|the realistic task is to)\s+/i, "")
    .replace(/^(?:i have|i've)\s+/i, ""));
  return cleaned ? `${cleaned.charAt(0).toLowerCase()}${cleaned.slice(1)}` : "";
}

function appendStructuredEvidence(summaryText = "", fields = {}) {
  let result = normalizeText(summaryText);
  const additions = [
    {
      kind: "objective",
      value: fields.participantObjective,
      sentence: fields.participantObjective
        ? `The member's objective was to ${actionForSummary(fields.participantObjective)}.`
        : "",
    },
    {
      kind: "agreed",
      value: fields.agreedActions?.[0],
      sentence: fields.agreedActions?.[0]
        ? `The agreed action was to ${actionForSummary(fields.agreedActions[0])}.`
        : "",
    },
    {
      kind: "completed",
      value: fields.completedActions?.[0],
      sentence: fields.completedActions?.[0]
        ? `The member explicitly confirmed the action was complete: ${cleanStructuredField(fields.completedActions[0])}.`
        : "",
    },
  ];
  additions.forEach(({ kind, value, sentence }) => {
    const covered = summaryCoversStructuredText(value, result);
    const completionCovered = kind !== "completed" ||
      (covered && /\b(explicitly confirmed|confirmed completing|completed|finished|wrote|written|done)\b/i.test(result));
    if (value && sentence && (!covered || !completionCovered)) {
      result = normalizeText(`${result} ${sentence}`);
    }
  });
  return preserveCanonicalSummaryText(result, summaryText);
}

function buildGroundedStructuredFields(messages = [], supplied = {}) {
  const transcript = messageText(messages).join(" ");
  const derivedObjective = extractParticipantObjective(messages);
  const suppliedObjective = cleanStructuredField(supplied?.participantObjective);
  const participantObjective = suppliedObjective && isGroundedStructuredText(suppliedObjective, transcript)
    ? suppliedObjective
    : derivedObjective;

  const derivedAgreedActions = extractAgreedActions(messages);
  const suppliedAgreedActions = uniqueStructuredList(supplied?.agreedActions)
    .filter((value) => isGroundedStructuredText(value, transcript));
  const derivedCompletedActions = extractCompletedActions(messages);
  const suppliedCompletedActions = derivedCompletedActions.length
    ? uniqueStructuredList(supplied?.completedActions)
      .filter((value) => (
        isGroundedStructuredText(value, transcript) &&
        derivedCompletedActions.some((evidence) => (
          completedActionMatchesEvidence(value, evidence)
        ))
      ))
    : [];
  const completedActions = suppliedCompletedActions.length
    ? suppliedCompletedActions
    : derivedCompletedActions;
  const agreedActions = uniqueStructuredList([
    ...suppliedAgreedActions,
    ...derivedAgreedActions,
  ]);

  const suppliedNextFocus = cleanStructuredField(
    supplied?.nextFollowUpFocus || supplied?.suggestedNextStep
  );
  const nextFollowUpFocus = suppliedNextFocus && isGroundedStructuredText(suppliedNextFocus, transcript)
    ? suppliedNextFocus
    : agreedActions[0]
      ? `Review what changed after the member followed through on: ${actionForSummary(agreedActions[0])}.`
      : "";

  return {
    participantObjective,
    agreedActions,
    completedActions,
    nextFollowUpFocus,
  };
}

function attachGroundedStructuredFields(summary = {}, supplied = {}, messages = []) {
  const fields = buildGroundedStructuredFields(messages, {
    ...summary,
    ...supplied,
  });
  const structuredContractSupplied = Boolean(
    supplied &&
    typeof supplied === "object" &&
    (
      Object.prototype.hasOwnProperty.call(supplied, "participantObjective") ||
      Object.prototype.hasOwnProperty.call(supplied, "agreedActions") ||
      Object.prototype.hasOwnProperty.call(supplied, "completedActions")
    )
  );
  return {
    ...summary,
    ...fields,
    nextFollowUpFocus: fields.nextFollowUpFocus || summary.nextFollowUpFocus || "",
    summaryText: summary?.fallbackUsed === false &&
      (structuredContractSupplied || fields.completedActions.length > 0)
      ? appendStructuredEvidence(summary.summaryText, fields)
      : summary.summaryText,
  };
}

function hasFocusPrioritisationTopic(text = "") {
  const haystack = normalizeText(text).toLowerCase();
  if (!haystack) return false;
  const focusSignal = /\b(?:focus(?:s|sing|ed)?|prioritis(?:e|ing|ed|ation)|prioritiz(?:e|ing|ed|ation))\b/i.test(haystack);
  const trackingSignal = /\b(?:notes?|list|book|forget|forgot|lost|lose|scattered|task tracking)\b/i.test(haystack);
  const actionSignal = /\b(urgent|important|notes?|list|book|prioritis(?:e|ing|ed|ation)|prioritiz(?:e|ing|ed|ation)|few|now)\b/i.test(haystack);
  return focusSignal && trackingSignal && actionSignal;
}

function buildFocusPrioritisationFallback(messages = []) {
  const userText = userConversationText(messages);
  const hasBook = /\bbook\b/i.test(userText);
  const hasList = /\blist\b/i.test(userText);
  const trackingText = hasBook && hasList
    ? "keeping scattered notes in one book, making one list"
    : hasBook
      ? "using one book to keep track of what needs attention"
      : hasList
        ? "making a workable short list"
        : "choosing what needs attention now";
  const nextStep = hasBook && hasList
    ? "Review the new book/list, then choose the few tasks that need attention now."
    : hasBook
      ? "Review the book, then choose the few tasks that need attention now."
      : hasList
        ? "Review the short list, then choose the few tasks that need attention now."
        : "Choose the few tasks that need attention now.";
  return {
    ...TOPIC_FALLBACKS.focusPrioritisation,
    summary: `The conversation focused on improving focus around urgent and important tasks, ${trackingText}, and prioritising only a few items for now.`,
    nextStep,
    nextFollowUpFocus: nextStep,
  };
}

function getTopicFallback(topic = "", messages = []) {
  if (topic === "focusPrioritisation") return buildFocusPrioritisationFallback(messages);
  return TOPIC_FALLBACKS[topic] || null;
}

function hasStrongTopicFallbackSupport(topic = "", messages = []) {
  const userText = userConversationText(messages);
  if (!topic || !userText) return false;
  if (topic === "identityBoundary") return hasIdentityBoundarySessionSignal(conversationText(messages));
  if (topic === "focusPrioritisation") return hasFocusPrioritisationTopic(userText);
  if (topic === "burnoutTunnelVision") {
    return /\b(burnout|burnt out|burned out|tunnel vision)\b/i.test(userText);
  }
  if (topic === "workloadStructure") return hasWorkloadStructureTopic(userText);
  if (topic === "familyPressure") return hasFamilyPressureTopic(userText);
  if (topic === "relationshipAttachment") {
    return /\b(relationship|dating|partner|ex|breakup|break up|attached|attachment|love her|love him|letting go|let go|reciprocat|mutual|connection|cofounder|co-founder|colleague|supplier|vendor|client)\b/i.test(userText);
  }
  if (topic === "highClinicalDissociation") return hasHighClinicalDissociationTopic(userText);
  return false;
}

function getGroundedTopicFallback(messages = []) {
  const topic = inferConversationTopic(messages);
  return hasStrongTopicFallbackSupport(topic, messages) ? getTopicFallback(topic, messages) : null;
}

function inferConversationTopic(messages = [], extraText = "") {
  const transcriptText = conversationText(messages);
  const userText = userConversationText(messages);
  if (hasSafetySessionSignal(messages)) return "safety";
  const haystack = normalizeText([transcriptText, extraText].filter(Boolean).join(" ")).toLowerCase();
  if (!haystack) return "";
  if (hasFocusPrioritisationTopic(userText)) return "focusPrioritisation";
  if (hasHighClinicalDissociationTopic(haystack)) return "highClinicalDissociation";
  if (hasIdentityBoundarySessionSignal(haystack)) return "identityBoundary";
  const hasRelationship = /\b(relationship|dating|partner|ex|breakup|break up|attached|attachment|love her|love him|letting go|let go|reciprocat|mutual|connection)\b/i.test(haystack);
  if (hasRelationship && /\b(always love|letting go|let go|attached|attachment|reciprocat|mutual|connection|miss her|miss him|grief|grieving)\b/i.test(haystack)) {
    return "relationshipAttachment";
  }
  if (/\b(partner|business partner|cofounder|co-founder|colleague|supplier|vendor|client)\b/i.test(haystack) && /\b(frustrat|irritat|annoy|angry|boundary|standard|professional|unprofessional|reciprocat|pulling weight|not contributing)\b/i.test(haystack)) {
    return "partnerBoundary";
  }
  if (/\b(burnout|burnt out|burned out|tunnel vision|falling behind|driven|drive|planned breaks?|rest|recovery|exhaust|drained|push through)\b/i.test(haystack)) {
    return "burnoutTunnelVision";
  }
  if (hasWorkloadStructureTopic(userText)) {
    return "workloadStructure";
  }
  if (hasFamilyPressureTopic(transcriptText || haystack)) {
    return "familyPressure";
  }
  if (hasAvoidanceActionBlockContext(haystack)) return "avoidance";
  return "";
}

function hasHighClinicalDissociationTopic(text = "") {
  const haystack = normalizeText(text).toLowerCase();
  if (!haystack) return false;
  const diagnosis = /\b(did|dissociative identity disorder|dissociation|dissociative)\b/i.test(haystack);
  const memoryOrControl = /\b(memory gaps?|los(?:e|ing|t) (?:chunks? of )?time|missing time|black(?:ed|ing)? out|wake(?:s|ing)? up (?:not knowing|and (?:i )?don'?t know)|don'?t know what i (?:did|said)|cannot remember|can'?t remember|switch(?:es|ing)?|parts? (?:fighting|taking over|in control|for control)|out of control|not in control)\b/i.test(haystack);
  const impact = /\b(partner|relationship|friends?|leave|abandon|unstable|shame|hiding|hide|panic|numb|daily life|work|control)\b/i.test(haystack);
  return diagnosis && memoryOrControl && impact;
}

function hasWorkloadStructureTopic(text = "") {
  return hasExplicitWorkloadStructureSignal(text);
}

function hasFamilyPressureTopic(text = "") {
  const haystack = normalizeText(text).toLowerCase();
  if (!haystack) return false;
  return /\b(family|parents?|mother|father)\b/i.test(haystack) &&
    /\b(pressure|expectation|expects?|responsib(?:le|ility)|provide|providing|support(?:ing)? everyone|everyone needs me|legacy|let down|believe in me|belive in me|proud|disappoint(?:ed|ing|ment)?)\b/i.test(haystack) &&
    !/\b(pain|medical costs?|medical aid|uncertainty|husband|injury|illness|doctor|hospital|treatment|medicine|medication)\b/i.test(haystack);
}

function hasIdentityBoundarySessionSignal(text = "") {
  const haystack = normalizeText(text).toLowerCase();
  if (!haystack) return false;
  const impersonationPrompt = /\b(pretend to be|act as|you are now|role[\s-]?play as|respond as|speak as|take on the role of|play the role of)\b.{0,120}\b(dr\.?|doctor|therapist|psychiatrist|psychologist|clinical psychologist|coach|counsellor|counselor|clinician|elena mbeki)\b/i.test(haystack)
    || /\byou are\s+(?:dr\.?|doctor|therapist|psychiatrist|psychologist|clinical psychologist|coach)\b/i.test(haystack)
    || /\bstart the conversation now\b.{0,120}\b(dr\.?|doctor|therapist|psychiatrist|psychologist|clinical psychologist|coach|roleplay|impersonat)\b/i.test(haystack);
  const niaBoundaryResponse = /\b(i can'?t|i cannot|i won'?t|i will not|not able to)\b.{0,80}\b(impersonat|pretend to be|adopt another person'?s identity|claim credentials|credentials i do not hold)\b/i.test(haystack)
    || /\bi'?m nia\b.{0,140}\b(impersonat|another person'?s identity|claim credentials|credentials i do not hold|as nia)\b/i.test(haystack);
  return Boolean(impersonationPrompt || niaBoundaryResponse);
}

function hasSubstantialCoachingAfterIdentityBoundary(messages = []) {
  const userLines = messageText(messages, "user");
  if (userLines.length < 3) return false;
  const laterText = userLines.slice(1).join(" ").toLowerCase();
  const emotionalSignals = [
    /\bexhaust(?:ed|ion)?\b|\btired\b|\bdrained\b|\bburn(?:t|ed) out\b/i,
    /\binvisible|replaceable|unseen|what'?s the point|disappear|loss of self|lost myself\b/i,
    /\bcaregiv(?:e|ing|er)|taking care of|work pressure|pressure at work|everyone needs me\b/i,
    /\breal help|need help|want help|support|not in danger|not unsafe\b/i,
  ].filter((pattern) => pattern.test(laterText)).length;
  const laterWordCount = laterText.split(/\s+/).filter(Boolean).length;
  return laterWordCount >= 25 && emotionalSignals >= 2;
}

function buildMixedIdentityBoundarySummary(messages = []) {
  const haystack = conversationText(messages);
  const themes = THEME_KEYWORDS
    .filter((entry) => entry.pattern.test(haystack))
    .map((entry) => entry.label);
  const hasCaregiving = /\bcaregiv(?:e|ing|er)|taking care of|support everyone|everyone needs me\b/i.test(haystack);
  const hasWork = /\bwork|job|pressure|replaceable\b/i.test(haystack);
  const pressure = [
    hasCaregiving ? "caregiving pressure" : "",
    hasWork ? "work pressure" : "",
  ].filter(Boolean).join(" and ");
  const pressureText = pressure ? `, with ${pressure} contributing to the load` : "";
  const supportText = /\bwhat'?s the point|disappear|real help|not in danger|not unsafe\b/i.test(haystack)
    ? " The member also signalled a need for real support while indicating they were not in immediate danger."
    : "";
  return {
    summaryText: `Nia clarified the identity boundary before continuing as Nia. The real coaching focus became exhaustion, feeling invisible or replaceable, and losing a sense of self${pressureText}.${supportText}`,
    suggestedNextStep: "Stay with the support need directly: name what feels most invisible right now, and identify one safe human support option if the feeling intensifies.",
    detectedThemes: mergeThemeLists(["identity boundary", "exhaustion", "caregiving pressure", "invisibility", "support needs"], themes),
  };
}

function stripFirstPersonLead(text = "") {
  return normalizeText(text)
    .replace(/^(i am|i'm|i was|i feel|i felt|i have been|i've been|i keep|i need|i want|my)\s+/i, "")
    .replace(/^trying to\s+/i, "")
    .replace(/[.?!]+$/g, "")
    .trim();
}

function isGenericClosingAcknowledgement(text = "") {
  const normalized = normalizeText(text).toLowerCase();
  if (!normalized) return true;
  const namesConcreteTopic = /\b(?:website|https|contact form|priority|task|problem|issue|meeting|relationship|work|family|health|money)\b/i.test(normalized);
  if (namesConcreteTopic) return false;
  return /^(?:that works|sounds good|okay|ok|great|thanks|thank you)\b/i.test(normalized) ||
    /^(?:i have|i've)\s+(?:written|wrote|noted|recorded)\s+(?:that|it|the action)\s+down\b/i.test(normalized);
}

function isSchedulingLogisticsLine(text = "") {
  const normalized = normalizeText(text);
  return Boolean(normalized && isSchedulingOnlySession([{ role: "user", content: normalized }]));
}

function inferEmotionalNuance(messages = []) {
  const haystack = conversationText(messages);
  const matches = [
    { label: "burnout", pattern: /\bburn(?:t|ed)\s*out|burnout\b/i },
    { label: "overwhelm", pattern: /\boverwhelm(?:ed|ing)?\b|\btoo much\b|\boverload(?:ed)?\b/i },
    { label: "avoidance", pattern: /\bavoid(?:ing|ance)?\b|\bprocrastinat(?:e|ing|ion)\b|\bput(?:ting)? off\b|\bstuck\b|\bcan't start\b|\bcannot start\b/i },
    { label: "exhaustion", pattern: /\bexhaust(?:ed|ion)?\b|\btired\b|\bfatigue(?:d)?\b|\bdrained\b|\blow energy\b|\benergy crash(?:es|ed)?\b/i },
    { label: "frustration", pattern: /\bfrustrat(?:ed|ing|ion)?\b|\birritat(?:ed|ing|ion)?\b|\bannoyed\b|\bangry\b/i },
    { label: "emotional suppression", pattern: /\bshut down\b|\bnumb\b|\bflat\b|\bpush(?:ing)? through\b|\bhold(?:ing)? it in\b|\bsuppress(?:ing|ed)?\b/i },
  ].filter((entry) => entry.pattern.test(haystack)).map((entry) => entry.label);
  return [...new Set(matches)];
}

function hasAvoidanceActionBlockContext(text = "") {
  const haystack = normalizeText(text).toLowerCase();
  if (!haystack) return false;
  return (
    /\b(procrastinat\w*|avoid(?:ing|ance|ed)?|delay(?:ing|ed)?|put(?:ting)? (?:it |this |that )?off|postpon(?:e|ing|ed)|stall(?:ing)?|not starting|can't start|cannot start|won't start|will not start|keep stopping|stop myself|stuck on starting|stuck before starting|follow through|do it later|tomorrow)\b/i.test(haystack) ||
    /\b(know|aware|realise|realize|notice|understand)\b.{0,90}\b(excuse|not acting|not moving|not starting|still blocked|still stuck|can't act|cannot act|can't move|cannot move|can't do it|cannot do it)\b/i.test(haystack)
  );
}

function hasAwarenessNoActionSummarySignal(messages = [], text = "") {
  const transcriptText = conversationText(messages);
  const haystack = normalizeText([transcriptText, text].filter(Boolean).join(" ")).toLowerCase();
  if (!haystack) return false;
  if (!hasAvoidanceActionBlockContext(transcriptText || haystack)) return false;
  const awareness = /\b(i know|i can see|i see|i understand|i'?m aware|i am aware|i realise|i realize|i get it|i notice|i noticed|aware of it|know the excuse|know my excuse|awareness alone|aware but)\b/i.test(haystack);
  const blockedAction = /\b(but|and yet|yet|still|even so)\b.{0,100}\b(can'?t|cannot|don'?t|do not|won'?t|will not|bring myself|move|act|start|do it|follow through)\b/i.test(haystack)
    || /\b(can'?t|cannot|don'?t|do not|won'?t|will not|bring myself)\b.{0,70}\b(act|move|start|do it|follow through)\b/i.test(haystack);
  const explicitAvoidanceAwareness = /\b(know|aware|realise|realize|notice|understand)\b.{0,90}\b(avoid(?:ing|ance)?|procrastinat\w*|delay(?:ing)?|put(?:ting)? off|excuse|not acting|not starting|still blocked|still stuck)\b/i.test(haystack);
  return (awareness && blockedAction) || explicitAvoidanceAwareness;
}

function buildAwarenessNoActionSummaryText() {
  return TOPIC_FALLBACKS.avoidance.summary;
}

function buildAwarenessNoActionNextStep() {
  return TOPIC_FALLBACKS.avoidance.nextStep;
}

function inferConversationFocus(messages = [], coachingState = {}) {
  const userLines = messageText(messages, "user");
  const substantiveUserLines = userLines.filter((line) => (
    !isGenericClosingAcknowledgement(line) && !isSchedulingLogisticsLine(line)
  ));
  const latestUser = substantiveUserLines[substantiveUserLines.length - 1] || "";
  const firstUser = userLines[0] || "";
  const candidates = [
    coachingState?.currentStory,
    coachingState?.presentingIssue,
    latestUser,
    firstUser,
  ]
    .filter((item) => !isGenericClosingAcknowledgement(item) && !isSchedulingLogisticsLine(item))
    .map((item) => stripFirstPersonLead(item))
    .filter(Boolean);
  const focus = candidates.find((item) => item.length >= 8) || "what felt difficult in the moment";
  return focus.length > 120 ? `${focus.slice(0, 117)}...` : focus;
}

export function buildNaturalSummaryText(messages = [], coachingState = {}) {
  if (hasSafetySessionSignal(messages)) {
    return buildSafetySessionSummary(true).summaryText;
  }
  if (hasThirdPersonSafetyReference(messages)) {
    return buildThirdPersonSafetyContextSummary(messages).summaryText;
  }
  if (hasFirstPersonHistoricalSafetyReference(messages)) {
    return buildHistoricalPersonalSafetySummary().summaryText;
  }
  if (hasExplicitSafetyDeescalation(messages)) {
    return buildExplicitSafetyDeescalationSummary().summaryText;
  }
  if (hasCumulativeSafetyAdjacentSessionSignal(messages)) {
    return buildCumulativeSafetyAdjacentSummary(messages).summaryText;
  }
  if (hasHumanCoachReviewRequest(messages)) {
    return buildHumanCoachReviewRequestSummary(messages).summaryText;
  }
  if (hasResolvedAmbiguousSafetyClarification(messages)) {
    return buildPracticalClarificationSummary().summaryText;
  }
  if (hasAwarenessNoActionSummarySignal(messages)) {
    return buildAwarenessNoActionSummaryText();
  }
  const topic = inferConversationTopic(messages);
  if (topic === "identityBoundary" && hasSubstantialCoachingAfterIdentityBoundary(messages)) {
    return buildMixedIdentityBoundarySummary(messages).summaryText;
  }
  const topicFallback = topic !== "avoidance" ? getGroundedTopicFallback(messages) : null;
  if (topicFallback) {
    return topicFallback.summary;
  }
  const focus = inferConversationFocus(messages, coachingState);
  const nuances = inferEmotionalNuance(messages);
  const primaryBlock = normalizeText(coachingState?.primaryBlock || "");
  const lead = nuances.includes("avoidance")
    ? "The member was struggling with"
    : nuances.includes("overwhelm") || nuances.includes("burnout") || nuances.includes("exhaustion")
      ? "The member described"
      : "The conversation focused on";
  const nuanceText = nuances.length
    ? `, with ${nuances.slice(0, 2).join(" and ")} showing up clearly`
    : "";
  const blockText = primaryBlock ? ` The main pattern was ${primaryBlock.toLowerCase()}.` : "";
  return preserveCanonicalSummaryText(
    normalizeText(`${lead} ${focus}${nuanceText}.${blockText}`)
  );
}

function cleanNextStepText(text = "") {
  const cleaned = cleanSupportGrammar(text)
    .replace(/^(?:the coach\s+)?(?:encourage|encourages|encouraged)\s+(?:the member|the client|you)\s+to\s+/i, "")
    .replace(/^(?:the coach\s+)?(?:suggest|suggests|suggested|recommend|recommends|recommended)\s+(?:that\s+)?(?:the member|the client|you)\s+/i, "")
    .replace(/^(?:the member|the client|you)\s+should\s+/i, "")
    .replace(/^the next step is to\s+/i, "")
    .replace(/^next step:\s*/i, "")
    .replace(/^try to\s+/i, "")
    .replace(/^to\s+/i, "")
    .replace(/\b(small manageable step|one small move|manageable action|break it down)\b/gi, "clear response")
    .trim();
  if (!cleaned) return "";
  const sentence = cleanSupportGrammar(cleaned.charAt(0).toUpperCase() + cleaned.slice(1));
  return /[.!?]$/.test(sentence) ? sentence : `${sentence}.`;
}

export function buildGroundedNextStep(messages = [], coachingState = {}) {
  if (hasSafetySessionSignal(messages)) {
    return buildSafetySessionSummary(true).suggestedNextStep;
  }
  if (hasThirdPersonSafetyReference(messages)) {
    return buildThirdPersonSafetyContextSummary(messages).suggestedNextStep;
  }
  if (hasFirstPersonHistoricalSafetyReference(messages)) {
    return buildHistoricalPersonalSafetySummary().suggestedNextStep;
  }
  if (hasExplicitSafetyDeescalation(messages)) {
    return buildExplicitSafetyDeescalationSummary().suggestedNextStep;
  }
  if (hasCumulativeSafetyAdjacentSessionSignal(messages)) {
    return buildCumulativeSafetyAdjacentSummary(messages).suggestedNextStep;
  }
  if (hasHumanCoachReviewRequest(messages)) {
    return buildHumanCoachReviewRequestSummary(messages).suggestedNextStep;
  }
  if (hasResolvedAmbiguousSafetyClarification(messages)) {
    return buildPracticalClarificationSummary().suggestedNextStep;
  }
  if (hasAwarenessNoActionSummarySignal(messages)) {
    return buildAwarenessNoActionNextStep();
  }
  const topic = inferConversationTopic(messages);
  if (topic === "identityBoundary" && hasSubstantialCoachingAfterIdentityBoundary(messages)) {
    return buildMixedIdentityBoundarySummary(messages).suggestedNextStep;
  }
  const topicFallback = getGroundedTopicFallback(messages);
  if (topicFallback?.nextStep) {
    return topicFallback.nextStep;
  }
  const nuances = inferEmotionalNuance(messages);
  const action = normalizeText(
    coachingState?.completionAction ||
    coachingState?.immediateTarget ||
    (Array.isArray(coachingState?.actions) ? coachingState.actions[0] : "")
  );
  if (action && !/^(a clearer, more workable next state|clarify the next concrete move|general support need)$/i.test(action)) {
    return cleanNextStepText(`Focus on ${action.replace(/^(my agreed action is(?: to)?|i will|i'll|i am going to|i'm going to|i plan to|i decided(?: to)?|i have decided to|i've decided to|my plan is(?: to)?|my decision is(?: to)?|my next step is|what i'm going to do is(?: to)?)\s+/i, "")}`);
  }
  if (nuances.includes("exhaustion") || nuances.includes("burnout")) {
    return "Focus on noticing the first moment your energy drops, and respond before pushing through it.";
  }
  if (nuances.includes("avoidance")) {
    return "Focus on the first moment you start to avoid the task, and name what makes starting feel unsafe.";
  }
  if (nuances.includes("overwhelm")) {
    return "Focus on the pressure point that makes everything feel urgent, then choose the one thing that actually needs attention first.";
  }
  if (nuances.includes("emotional suppression")) {
    return "Focus on catching the moment you shut the feeling down, and give it a clear name before acting.";
  }
  if (nuances.includes("frustration")) {
    return "Focus on what the frustration is pointing to, then choose the response that reduces pressure rather than adds more.";
  }
  return cleanNextStepText(buildStepAlignedFollowUp(inferCurrentStep(messages)));
}

function makeTitle(messages) {
  const firstUser = messages.find((m) => m.role === "user");
  if (!firstUser?.content) return "Coaching Session";
  const s = normalizeText(firstUser.content);
  return s.length > 60 ? `${s.slice(0, 57)}…` : s;
}

export function inferThemes(messages = []) {
  const haystack = conversationText(messages);
  if (hasSafetySessionSignal(messages)) {
    return buildSafetySessionSummary(true).detectedThemes;
  }
  if (hasThirdPersonSafetyReference(messages)) {
    return buildThirdPersonSafetyContextSummary(messages).detectedThemes;
  }
  if (hasFirstPersonHistoricalSafetyReference(messages)) {
    return buildHistoricalPersonalSafetySummary().detectedThemes;
  }
  if (hasExplicitSafetyDeescalation(messages)) {
    return buildExplicitSafetyDeescalationSummary().detectedThemes;
  }
  if (hasCumulativeSafetyAdjacentSessionSignal(messages)) {
    return buildCumulativeSafetyAdjacentSummary(messages).detectedThemes;
  }
  if (hasHumanCoachReviewRequest(messages)) {
    return buildHumanCoachReviewRequestSummary(messages).detectedThemes;
  }
  if (hasResolvedAmbiguousSafetyClarification(messages)) {
    return buildPracticalClarificationSummary().detectedThemes;
  }
  const topic = inferConversationTopic(messages);
  if (topic === "identityBoundary" && hasSubstantialCoachingAfterIdentityBoundary(messages)) {
    return buildMixedIdentityBoundarySummary(messages).detectedThemes;
  }
  const topicFallback = getGroundedTopicFallback(messages);
  if (topicFallback?.themes) {
    return topicFallback.themes;
  }
  const found = THEME_KEYWORDS
    .filter((entry) => entry.pattern.test(haystack))
    .map((entry) => entry.label)
    .filter((label, index, list) => list.indexOf(label) === index)
    .slice(0, 4);
  return found.length ? found : ["general support"];
}

function inferFlagSuggested(messages = []) {
  return hasSafetySessionSignal(messages) || hasCumulativeSafetyAdjacentSessionSignal(messages);
}

function followUpDateValue(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  if (typeof value?.toMillis === "function") return new Date(value.toMillis());
  if (typeof value?.seconds === "number") return new Date(value.seconds * 1000);
  return new Date(value);
}

function activeFollowUpScheduleLabel(followUp = null) {
  if (String(followUp?.status || "").toLowerCase() !== "scheduled" || !followUp?.scheduledFor) return "";
  const parsed = followUpDateValue(followUp.scheduledFor);
  if (!parsed || Number.isNaN(parsed.getTime())) return "";
  return new Intl.DateTimeFormat("en-ZA", {
    timeZone: "Africa/Johannesburg",
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(parsed);
}

function sanitizeSchedulingCoachingState(coachingState = {}, nextFollowUpFocus = "") {
  return {
    ...coachingState,
    promptPriority: "default",
    forceChallenge: false,
    repeatPhrases: [],
    obstacleFirst: false,
    forceObstacleFirst: false,
    persistentObstacle: false,
    predictedOutcomeStory: false,
    primaryStoppingQuestion: "",
    primaryBlock: "",
    internalObstacles: [],
    externalObstacles: [],
    limitingBeliefs: [],
    distortionPatterns: [],
    targetSkills: [],
    recommendedGames: [],
    gameRationales: [],
    nextFollowUpFocus,
  };
}

function buildSchedulingOnlySummary(messages = [], coachingState = {}, options = {}) {
  const scheduleLabel = activeFollowUpScheduleLabel(options.followUp);
  const userTurnCount = messageText(messages, "user").length;
  const arrangementText = userTurnCount > 1 ? "schedule and update" : "arrange";
  const summaryText = scheduleLabel
    ? `The conversation was used to ${arrangementText} a follow-up. The active follow-up is scheduled for ${scheduleLabel}.`
    : `The conversation was used to ${arrangementText} a follow-up.`;
  const nextStep = scheduleLabel
    ? `Return for the active follow-up on ${scheduleLabel}.`
    : "Continue with the current follow-up request, clarifying the date or time if needed.";
  return {
    summaryText,
    detectedThemes: [],
    suggestedNextStep: nextStep,
    flagSuggested: false,
    currentStep: coachingState.currentStep || inferCurrentStep(messages),
    detectedBlocks: [],
    targetSkills: [],
    recommendedGames: [],
    gameRationales: [],
    coachingState: sanitizeSchedulingCoachingState(coachingState, nextStep),
    nextFollowUpFocus: nextStep,
    generatedAt: options.generatedAt || Date.now(),
    source: options.source || "scheduling-only-rule",
    fallbackUsed: true,
  };
}

const OPERATIONAL_SCHEDULE_REFERENCE_PATTERN = /(?:\b(?:scheduled\s+for|return\s+plan|follow[-\s]?up\s+(?:on|at|for)|reminder\s+(?:on|at|for))\b|\b(?:today|tomorrow|monday|tuesday|wednesday|thursday|friday|saturday|sunday|\d{1,2}(?:st|nd|rd|th)?\s+(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?))\b.{0,55}\b\d{1,2}(?::|\.)?\d{0,2}\s*(?:am|pm|a\.m\.|p\.m\.)?\b)/i;

function hasOperationalScheduleReference(value = "") {
  return OPERATIONAL_SCHEDULE_REFERENCE_PATTERN.test(normalizeText(value));
}

function removeLegacyScheduleReference(value = "") {
  const normalized = normalizeText(value)
    .replace(/\s*,?\s*(?:and\s+)?(?:the\s+)?(?:17\s+july|today|tomorrow|monday|tuesday|wednesday|thursday|friday|saturday|sunday)(?:\s*,?\s*\d{1,2}\s+(?:january|february|march|april|may|june|july|august|september|october|november|december)\s+\d{4})?\s+(?:at\s+)?\d{1,2}(?::|\.)?\d{0,2}\s*(?:am|pm|a\.m\.|p\.m\.)?\s+return\s+plan/gi, "")
    .replace(/\s+/g, " ")
    .replace(/\s+([,.])/g, "$1")
    .trim();
  if (!hasOperationalScheduleReference(normalized)) return normalized;
  return normalized
    .split(/(?<=[.!?])\s+/)
    .filter((sentence) => !hasOperationalScheduleReference(sentence))
    .join(" ")
    .trim();
}

export function reconcileSummaryWithActiveFollowUp(summary = {}, followUp = null, messages = []) {
  if (!summary || typeof summary !== "object") return summary;
  const scheduleLabel = activeFollowUpScheduleLabel(followUp);
  if (isSchedulingOnlySession(messages)) {
    return buildSchedulingOnlySummary(messages, summary.coachingState || {}, {
      followUp,
      generatedAt: Number(summary.generatedAt) || Date.now(),
      source: "scheduling-only-rule",
    });
  }
  if (!scheduleLabel) return summary;

  const scheduleSentence = `The active follow-up is scheduled for ${scheduleLabel}.`;
  const scheduleAction = `Return for the active follow-up on ${scheduleLabel}.`;
  const next = { ...summary };
  if (hasOperationalScheduleReference(next.summaryText)) {
    const withoutLegacySchedule = removeLegacyScheduleReference(next.summaryText);
    next.summaryText = normalizeText(`${withoutLegacySchedule} ${scheduleSentence}`);
  }
  if (hasOperationalScheduleReference(next.suggestedNextStep)) next.suggestedNextStep = scheduleAction;
  if (hasOperationalScheduleReference(next.nextFollowUpFocus)) next.nextFollowUpFocus = scheduleAction;
  if (next.coachingState && typeof next.coachingState === "object") {
    next.coachingState = {
      ...next.coachingState,
      nextFollowUpFocus: hasOperationalScheduleReference(next.coachingState.nextFollowUpFocus)
        ? scheduleAction
        : next.coachingState.nextFollowUpFocus,
    };
  }
  return next;
}

export function generateSessionSummary(messages = []) {
  const userLines = messageText(messages, "user");
  const niaLines = messageText(messages, "assistant");

  const userKey = preserveCanonicalSummaryText(userLines[0] || "") || "—";
  const niaKey = preserveCanonicalSummaryText(niaLines[0] || "") || "—";

  return [
    "Session Summary:",
    `• Client focus: ${userKey}`,
    `• NIA response: ${niaKey}`,
    `• Turns: ${messages.length}`,
  ].join("\n");
}

export function buildStructuredFallbackSummary(messages = [], options = {}) {
  const safetySession = hasSafetySessionSignal(messages);
  const contextualSafetyState = getSafetyContextState(messages);
  const resolvedSafetyContext = contextualSafetyState.explicitDeescalation && !contextualSafetyState.activePersonalSafety;
  const coachingState = buildCoachingState({ messages });
  const currentStep = coachingState.currentStep || inferCurrentStep(messages);
  if (safetySession) {
    const safetySummary = buildSafetySessionSummary(true);
    return {
      ...safetySummary,
      currentStep,
      detectedBlocks: [],
      targetSkills: [],
      recommendedGames: [],
      gameRationales: [],
      coachingState,
      nextFollowUpFocus: safetySummary.suggestedNextStep,
      generatedAt: options.generatedAt || Date.now(),
      source: options.source || "fallback-rule",
      fallbackUsed: true,
    };
  }
  if (hasThirdPersonSafetyReference(messages)) {
    const otherPersonSummary = buildThirdPersonSafetyContextSummary(messages);
    return {
      ...otherPersonSummary,
      currentStep,
      detectedBlocks: [],
      targetSkills: [],
      recommendedGames: [],
      gameRationales: [],
      coachingState: {
        ...coachingState,
        safetyEscalation: {
          ...(coachingState?.safetyEscalation || {}),
          safetySignal: false,
          reviewFlagRecommended: false,
          safetyContextClassification: getSafetyContextState(messages).classification,
        },
        nextFollowUpFocus: otherPersonSummary.nextFollowUpFocus,
      },
      generatedAt: options.generatedAt || Date.now(),
      source: options.source || "contextual-safety-rule",
      fallbackUsed: true,
    };
  }
  if (hasFirstPersonHistoricalSafetyReference(messages)) {
    const historicalSummary = buildHistoricalPersonalSafetySummary();
    return {
      ...historicalSummary,
      currentStep,
      detectedBlocks: [],
      targetSkills: [],
      recommendedGames: [],
      gameRationales: [],
      coachingState: {
        ...coachingState,
        safetyEscalation: {
          ...(coachingState?.safetyEscalation || {}),
          safetySignal: false,
          reviewFlagRecommended: false,
          historicalSafetySignal: true,
        },
        nextFollowUpFocus: historicalSummary.nextFollowUpFocus,
      },
      generatedAt: options.generatedAt || Date.now(),
      source: options.source || "contextual-safety-rule",
      fallbackUsed: true,
    };
  }
  if (hasExplicitSafetyDeescalation(messages)) {
    const deescalatedSummary = buildExplicitSafetyDeescalationSummary();
    return {
      ...deescalatedSummary,
      currentStep,
      detectedBlocks: [],
      targetSkills: [],
      recommendedGames: [],
      gameRationales: [],
      coachingState: {
        ...coachingState,
        safetyEscalation: {
          ...(coachingState?.safetyEscalation || {}),
          safetySignal: false,
          reviewFlagRecommended: false,
          explicitSafetyDeescalation: true,
        },
        nextFollowUpFocus: deescalatedSummary.nextFollowUpFocus,
      },
      generatedAt: options.generatedAt || Date.now(),
      source: options.source || "contextual-safety-rule",
      fallbackUsed: true,
    };
  }
  if (hasCumulativeSafetyAdjacentSessionSignal(messages)) {
    const cumulativeSummary = buildCumulativeSafetyAdjacentSummary(messages);
    return {
      ...cumulativeSummary,
      currentStep,
      detectedBlocks: [],
      targetSkills: [],
      recommendedGames: [],
      gameRationales: [],
      coachingState,
      generatedAt: options.generatedAt || Date.now(),
      source: options.source || "fallback-rule",
      fallbackUsed: true,
    };
  }
  if (classifyOperationalIntent(messages).operational) {
    return buildOperationalSupportSummary(messages, coachingState, options);
  }
  if (classifyFactualPlanningIntent(messages).factualPlanning) {
    return buildRetirementPlanningSummary(messages, coachingState, options);
  }
  if (hasHumanCoachReviewRequest(messages)) {
    const reviewSummary = buildHumanCoachReviewRequestSummary(messages);
    return {
      ...reviewSummary,
      currentStep,
      detectedBlocks: [],
      targetSkills: [],
      recommendedGames: [],
      gameRationales: [],
      coachingState,
      generatedAt: options.generatedAt || Date.now(),
      source: options.source || "fallback-rule",
      fallbackUsed: true,
    };
  }
  if (hasResolvedAmbiguousSafetyClarification(messages)) {
    const practicalSummary = buildPracticalClarificationSummary();
    return {
      ...practicalSummary,
      currentStep,
      detectedBlocks: [],
      targetSkills: [],
      recommendedGames: [],
      gameRationales: [],
      coachingState,
      generatedAt: options.generatedAt || Date.now(),
      source: options.source || "fallback-rule",
      fallbackUsed: true,
    };
  }
  const websiteQualityCheckSummary = buildWebsiteQualityCheckSummary(messages, {
    generatedAt: options.generatedAt,
    source: options.source,
    currentStep,
  });
  if (websiteQualityCheckSummary) {
    return websiteQualityCheckSummary;
  }
  if (isSchedulingOnlySession(messages)) {
    return buildSchedulingOnlySummary(messages, coachingState, options);
  }
  if (isLowContextNonSafetySession(messages)) {
    return {
      ...buildLowContextSummary(options),
      currentStep,
      detectedBlocks: [],
      targetSkills: [],
      recommendedGames: [],
      gameRationales: [],
      coachingState,
    };
  }
  const structuredFields = buildGroundedStructuredFields(messages);
  const eligibleExtractiveUserLines = messageText(messages, "user")
    .filter((line) => !/\b(i (?:did not|didn't) ask (?:for|about) (?:that|this)|you(?:'re| are) not listening(?: again)?|are you not connected to the coaching engine)\b/i.test(line));
  const extractiveUserLines = eligibleExtractiveUserLines.length <= 3
    ? eligibleExtractiveUserLines
    : [eligibleExtractiveUserLines[0], ...eligibleExtractiveUserLines.slice(-2)];
  const extractiveSummaryText = extractiveUserLines.length
    ? extractiveUserLines
      .map((line) => `The member said: “${line.replace(/[.!?]+$/, "")}”.`)
      .join(" ")
    : "The session did not contain enough participant evidence for an extractive summary.";
  const extractiveNextStep = structuredFields.agreedActions[0]
    ? cleanNextStepText(actionForSummary(structuredFields.agreedActions[0]))
    : "";
  const extractiveThemeText = userConversationText(messages);
  const extractiveThemes = THEME_KEYWORDS
    .filter((entry) => entry.pattern.test(extractiveThemeText))
    .map((entry) => entry.label)
    .filter((label, index, labels) => labels.indexOf(label) === index)
    .slice(0, 4);
  const extractiveCoachingState = {
    ...coachingState,
    repeatPhrases: [],
    obstacleFirst: false,
    forceObstacleFirst: false,
    persistentObstacle: false,
    predictedOutcomeStory: false,
    predictedOutcomeQuestion: "",
    obstacleQuestion: "",
    primaryBlock: "",
    primaryStoppingQuestion: "",
    internalObstacles: [],
    limitingBeliefs: [],
    distortionPatterns: [],
    targetSkills: [],
    recommendedGames: [],
    gameRationales: [],
    futureState: "",
    immediateTarget: extractiveNextStep,
    supportNeeds: [],
    actions: structuredFields.agreedActions,
    nextFollowUpFocus: extractiveNextStep,
  };
  return {
    summaryText: preserveCanonicalSummaryText(extractiveSummaryText),
    detectedThemes: extractiveThemes,
    suggestedNextStep: extractiveNextStep,
    flagSuggested: resolvedSafetyContext
      ? false
      : inferFlagSuggested(messages),
    currentStep,
    detectedBlocks: [],
    targetSkills: [],
    recommendedGames: [],
    gameRationales: [],
    ...structuredFields,
    coachingState: extractiveCoachingState,
    nextFollowUpFocus: extractiveNextStep,
    generatedAt: options.generatedAt || Date.now(),
    source: options.source || "extractive-fallback-rule",
    fallbackUsed: true,
  };
}

function sanitizeThemeList(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => normalizeText(item))
    .filter(Boolean)
    .slice(0, 5);
}

function mergeThemeLists(primary = [], fallback = []) {
  const generic = new Set(["stress", "energy", "general support"]);
  const merged = [...fallback, ...primary]
    .map((item) => normalizeText(item).toLowerCase())
    .filter(Boolean)
    .filter((item, index, list) => list.indexOf(item) === index);
  const specific = merged.filter((item) => !generic.has(item));
  const general = merged.filter((item) => generic.has(item));
  return [...specific, ...general].slice(0, 5);
}

function refineSummaryText(text = "", fallbackText = "") {
  const safeFallback = preserveCanonicalSummaryText(fallbackText);
  const base = normalizeText(text || safeFallback);
  if (!base) return safeFallback;
  if (isLikelyLegacyTruncatedSummary(base)) return safeFallback;
  const cleaned = cleanSupportGrammar(rewriteAll(base, [
    [/^This session indicates(?: that)?\s+/i, "The conversation focused on "],
    [/^This session shows(?: that)?\s+/i, "The conversation focused on "],
    [/^The client reported feeling\b/i, "The member described feeling"],
    [/^The client reported(?: that)?\b/i, "The member noticed"],
    [/^The client mentioned(?: that)?\b/i, "The member described"],
    [/^The client said feeling\b/i, "The member described feeling"],
    [/^The client said(?: that)?\b/i, "The member described"],
    [/\bstress and stress\b/gi, "stress"],
    [/\bsmall steps\b/gi, "a clearer response"],
  ]));
  return preserveCanonicalSummaryText(cleaned, safeFallback);
}

function normalizeStructuredSummaryBase(summary, messages = [], options = {}) {
  if (!summary || typeof summary !== "object") {
    return buildStructuredFallbackSummary(messages, options);
  }
  const fallback = buildStructuredFallbackSummary(messages, options);
  const contextualSafetyState = getSafetyContextState(messages);
  const resolvedSafetyContext = contextualSafetyState.explicitDeescalation && !contextualSafetyState.activePersonalSafety;
  if (hasSafetySessionSignal(messages)) {
    return {
      ...fallback,
      ...buildSafetySessionSummary(true),
      currentStep: fallback.currentStep,
      detectedBlocks: [],
      targetSkills: [],
      recommendedGames: [],
      gameRationales: [],
      coachingState: fallback.coachingState,
      nextFollowUpFocus: buildSafetySessionSummary(true).suggestedNextStep,
      generatedAt: Number(summary.generatedAt) || Date.now(),
      source: normalizeText(summary.source || summary.model || fallback.source).slice(0, 80) || fallback.source,
      fallbackUsed: typeof summary.fallbackUsed === "boolean" ? summary.fallbackUsed : fallback.fallbackUsed,
    };
  }
  if (hasThirdPersonSafetyReference(messages)) {
    const otherPersonSummary = buildThirdPersonSafetyContextSummary(messages);
    return {
      ...fallback,
      ...otherPersonSummary,
      currentStep: fallback.currentStep,
      detectedBlocks: [],
      targetSkills: [],
      recommendedGames: [],
      gameRationales: [],
      coachingState: fallback.coachingState,
      generatedAt: Number(summary.generatedAt) || Date.now(),
      source: "contextual-safety-rule",
      fallbackUsed: true,
    };
  }
  if (hasFirstPersonHistoricalSafetyReference(messages)) {
    const historicalSummary = buildHistoricalPersonalSafetySummary();
    return {
      ...fallback,
      ...historicalSummary,
      currentStep: fallback.currentStep,
      detectedBlocks: [],
      targetSkills: [],
      recommendedGames: [],
      gameRationales: [],
      coachingState: fallback.coachingState,
      generatedAt: Number(summary.generatedAt) || Date.now(),
      source: "contextual-safety-rule",
      fallbackUsed: true,
    };
  }
  if (hasExplicitSafetyDeescalation(messages)) {
    const deescalatedSummary = buildExplicitSafetyDeescalationSummary();
    return {
      ...fallback,
      ...deescalatedSummary,
      currentStep: fallback.currentStep,
      detectedBlocks: [],
      targetSkills: [],
      recommendedGames: [],
      gameRationales: [],
      coachingState: fallback.coachingState,
      generatedAt: Number(summary.generatedAt) || Date.now(),
      source: "contextual-safety-rule",
      fallbackUsed: true,
    };
  }
  if (hasCumulativeSafetyAdjacentSessionSignal(messages)) {
    const cumulativeSummary = buildCumulativeSafetyAdjacentSummary(messages);
    return {
      ...fallback,
      ...cumulativeSummary,
      currentStep: fallback.currentStep,
      detectedBlocks: [],
      targetSkills: [],
      recommendedGames: [],
      gameRationales: [],
      coachingState: fallback.coachingState,
      generatedAt: Number(summary.generatedAt) || Date.now(),
      source: normalizeText(summary.source || summary.model || fallback.source).slice(0, 80) || fallback.source,
      fallbackUsed: typeof summary.fallbackUsed === "boolean" ? summary.fallbackUsed : fallback.fallbackUsed,
    };
  }
  if (classifyOperationalIntent(messages).operational) {
    return {
      ...fallback,
      generatedAt: Number(summary.generatedAt) || fallback.generatedAt,
      source: "operational-support-rule",
      fallbackUsed: true,
    };
  }
  if (classifyFactualPlanningIntent(messages).factualPlanning) {
    return {
      ...fallback,
      generatedAt: Number(summary.generatedAt) || fallback.generatedAt,
      source: "factual-retirement-planning-rule",
      fallbackUsed: true,
    };
  }
  if (hasHumanCoachReviewRequest(messages)) {
    const reviewSummary = buildHumanCoachReviewRequestSummary(messages);
    return {
      ...fallback,
      ...reviewSummary,
      currentStep: fallback.currentStep,
      detectedBlocks: [],
      targetSkills: [],
      recommendedGames: [],
      gameRationales: [],
      coachingState: fallback.coachingState,
      nextFollowUpFocus: reviewSummary.nextFollowUpFocus,
      generatedAt: Number(summary.generatedAt) || Date.now(),
      source: reviewSummary.source || fallback.source,
      fallbackUsed: true,
    };
  }
  if (isSchedulingOnlySession(messages)) {
    return buildSchedulingOnlySummary(messages, fallback.coachingState, {
      ...options,
      generatedAt: Number(summary.generatedAt) || fallback.generatedAt,
      source: "scheduling-only-rule",
    });
  }
  if (isLowContextNonSafetySession(messages)) {
    return {
      ...fallback,
      ...buildLowContextSummary({
        generatedAt: Number(summary.generatedAt) || fallback.generatedAt,
        source: "low-context-placeholder",
      }),
      currentStep: fallback.currentStep,
      detectedBlocks: [],
      targetSkills: [],
      recommendedGames: [],
      gameRationales: [],
      coachingState: fallback.coachingState,
    };
  }
  const generatedSummaryText = [
    summary.summaryText,
    summary.suggestedNextStep,
    ...(Array.isArray(summary.detectedThemes) ? summary.detectedThemes : []),
    summary.participantObjective,
    ...(Array.isArray(summary.agreedActions) ? summary.agreedActions : []),
    ...(Array.isArray(summary.completedActions) ? summary.completedActions : []),
    summary.nextFollowUpFocus,
  ].filter(Boolean).join(" ");
  if (
    resolvedSafetyContext &&
    /\b(?:member shared a safety concern|self[-\s]?harm-adjacent|confirm immediate safety|human support|coach review|personal safety concern)\b/i.test(generatedSummaryText)
  ) {
    return {
      ...fallback,
      flagSuggested: false,
      nextFollowUpFocus: fallback.nextFollowUpFocus || fallback.suggestedNextStep,
      generatedAt: Number(summary.generatedAt) || fallback.generatedAt,
      source: "post-safety-clarification-fallback",
      fallbackUsed: true,
    };
  }
  if (hasUnsupportedGeneratedTheme(generatedSummaryText, messages)) {
    const fallbackText = `${fallback.summaryText || ""} ${fallback.suggestedNextStep || ""} ${(fallback.detectedThemes || []).join(" ")} ${fallback.nextFollowUpFocus || ""}`;
    if (!hasUnsupportedGeneratedTheme(fallbackText, messages)) {
      return {
        ...fallback,
        generatedAt: Number(summary.generatedAt) || fallback.generatedAt,
        source: "current-session-fallback",
        fallbackUsed: true,
      };
    }
    const neutral = buildLowContextSummary({
      generatedAt: Number(summary.generatedAt) || fallback.generatedAt,
      source: "low-context-placeholder",
    });
    return {
      ...fallback,
      ...neutral,
      detectedThemes: [],
      coachingState: {
        ...fallback.coachingState,
        nextFollowUpFocus: "",
      },
    };
  }
  const awarenessNoAction = hasAwarenessNoActionSummarySignal(messages, `${summary.summaryText || ""} ${summary.suggestedNextStep || ""}`);
  const topic = inferConversationTopic(messages);
  const identityBoundary = topic === "identityBoundary";
  const mixedIdentitySummary =
    identityBoundary && hasSubstantialCoachingAfterIdentityBoundary(messages)
      ? buildMixedIdentityBoundarySummary(messages)
      : null;
  const topicFallback = (!awarenessNoAction || identityBoundary) && topic && topic !== "avoidance" && topic !== "workloadStructure" && hasStrongTopicFallbackSupport(topic, messages)
    ? getTopicFallback(topic, messages)
    : null;
  const rawSummaryText = mixedIdentitySummary?.summaryText || (identityBoundary ? topicFallback.summary : (awarenessNoAction ? buildAwarenessNoActionSummaryText() : (topicFallback?.summary || summary.summaryText)));
  const rawNextStep = mixedIdentitySummary?.suggestedNextStep || (identityBoundary ? topicFallback.nextStep : (awarenessNoAction ? buildAwarenessNoActionNextStep() : (topicFallback?.nextStep || summary.suggestedNextStep)));
  const summaryText = refineSummaryText(rawSummaryText, fallback.summaryText);
  const suggestedNextStep = cleanNextStepText(rawNextStep || fallback.suggestedNextStep) || fallback.suggestedNextStep;
  const aiThemes = sanitizeThemeList(summary.detectedThemes);
  return reconcileSummaryWithActiveFollowUp({
    summaryText: formatSummaryTextForAudience(summaryText || fallback.summaryText, "coach"),
    detectedThemes: mixedIdentitySummary?.detectedThemes
      ? mixedIdentitySummary.detectedThemes
      : topicFallback?.themes
      ? topicFallback.themes
      : aiThemes.length
      ? mergeThemeLists(aiThemes, fallback.detectedThemes)
      : fallback.detectedThemes,
    suggestedNextStep,
    flagSuggested: resolvedSafetyContext
      ? false
      : typeof summary.flagSuggested === "boolean"
        ? summary.flagSuggested
        : fallback.flagSuggested,
    currentStep: summary.currentStep && typeof summary.currentStep === "object"
      ? {
          id: normalizeText(summary.currentStep.id || fallback.currentStep?.id) || fallback.currentStep?.id,
          index: Number(summary.currentStep.index) || fallback.currentStep?.index,
          confidence: normalizeText(summary.currentStep.confidence || fallback.currentStep?.confidence) || fallback.currentStep?.confidence,
          reason: normalizeText(summary.currentStep.reason || fallback.currentStep?.reason).slice(0, 220) || fallback.currentStep?.reason,
        }
      : fallback.currentStep,
    detectedBlocks: Array.isArray(summary.detectedBlocks)
      ? summary.detectedBlocks.map((item) => normalizeText(item)).filter(Boolean).slice(0, 8)
      : fallback.detectedBlocks,
    targetSkills: Array.isArray(summary.targetSkills)
      ? summary.targetSkills.map((item) => normalizeText(item)).filter(Boolean).slice(0, 5)
      : fallback.targetSkills,
    recommendedGames: Array.isArray(summary.recommendedGames)
      ? summary.recommendedGames.map((item) => normalizeText(item)).filter(Boolean).slice(0, 4)
      : fallback.recommendedGames,
    gameRationales: Array.isArray(summary.gameRationales)
      ? summary.gameRationales.map((item) => normalizeText(item)).filter(Boolean).slice(0, 4)
      : fallback.gameRationales,
    coachingState: summary.coachingState && typeof summary.coachingState === "object"
      ? {
          ...fallback.coachingState,
          ...summary.coachingState,
          nextFollowUpFocus: topicFallback?.nextFollowUpFocus || topicFallback?.nextStep || summary.coachingState.nextFollowUpFocus || fallback.coachingState?.nextFollowUpFocus,
          currentStep: summary.coachingState.currentStep && typeof summary.coachingState.currentStep === "object"
            ? {
                ...fallback.coachingState?.currentStep,
                ...summary.coachingState.currentStep,
              }
            : fallback.coachingState?.currentStep,
        }
      : topicFallback
        ? {
            ...fallback.coachingState,
            nextFollowUpFocus: topicFallback.nextFollowUpFocus || topicFallback.nextStep || fallback.coachingState?.nextFollowUpFocus,
          }
        : fallback.coachingState,
    nextFollowUpFocus: normalizeText(topicFallback?.nextFollowUpFocus || topicFallback?.nextStep || summary.nextFollowUpFocus || fallback.nextFollowUpFocus) || fallback.nextFollowUpFocus,
    generatedAt: Number(summary.generatedAt) || Date.now(),
    source: normalizeText(summary.source || summary.model || fallback.source).slice(0, 80) || fallback.source,
    fallbackUsed: typeof summary.fallbackUsed === "boolean" ? summary.fallbackUsed : fallback.fallbackUsed,
  }, options.followUp, messages);
}

export function normalizeStructuredSummary(summary, messages = [], options = {}) {
  const status = normalizeText(summary?.generationStatus || summary?.status || "").toLowerCase();
  const sourceMessageCount = Number(summary?.sourceMessageCount);
  const transcriptAdvanced = Number.isFinite(sourceMessageCount) &&
    sourceMessageCount !== (Array.isArray(messages) ? messages.length : 0);
  const explicitlyStale = summary?.isCurrent === false || ["stale", "superseded"].includes(status);
  if (summary && typeof summary === "object" && (transcriptAdvanced || explicitlyStale)) {
    return {
      ...buildStructuredFallbackSummary(messages, {
        ...options,
        source: "current-session-transcript-advanced",
      }),
      generationStatus: "draft",
      isCurrent: true,
      staleReason: "",
      staleDetectedAt: null,
    };
  }
  return preserveSummaryAuditFields(
    attachGroundedStructuredFields(
      normalizeStructuredSummaryBase(summary, messages, options),
      summary,
      messages
    ),
    summary
  );
}

export function normalizeSessionRecordSummary(record = {}) {
  const messages = Array.isArray(record?.messages) ? record.messages : [];
  if (!messages.length) {
    if (isLikelyLegacyTruncatedSummary(record?.sessionSummary?.summaryText)) return null;
    return reconcileSummaryWithActiveFollowUp(record?.sessionSummary || null, record?.followUp || null, messages);
  }
  return normalizeStructuredSummary(
    record?.sessionSummary || buildStructuredFallbackSummary(messages, { followUp: record?.followUp || null }),
    messages,
    { followUp: record?.followUp || null }
  );
}

export async function generateStructuredSessionSummary(messages = [], followUp = null) {
  const fallback = buildStructuredFallbackSummary(messages, { followUp });
  try {
    const response = await fetch(SUMMARY_API_PATH, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ messages, followUp }),
    });
    if (!response.ok) throw new Error(`API ${response.status}`);
    const payload = await response.json();
    return normalizeStructuredSummary(payload?.summary, messages, { followUp });
  } catch {
    return fallback;
  }
}

export function formatSessionSummaryForEmail(summary, messages = [], followUp = null, audience = "user") {
  const normalized = normalizeStructuredSummary(summary, messages, { followUp });
  const summaryText = formatSummaryTextForAudience(normalized.summaryText, audience);
  const followUpState = getFollowUpState(followUp);
  const followUpLines = !followUpState.hasFollowUp
    ? []
    : (
      followUpState.label === "suggested"
        ? [
            "",
            "Follow-up status: Saved as a suggestion in Nia COACH.",
            "Reminder status: No email reminder is currently queued.",
          ]
        : followUpState.label === "scheduled_reminder_sent"
          ? [
              "",
              "Follow-up status: Scheduled in Nia COACH.",
              "Reminder status: Reminder email already sent.",
            ]
          : followUpState.label === "scheduled_reminder_queued"
            ? [
                "",
                "Follow-up status: Scheduled in Nia COACH.",
                "Reminder status: Reminder email queued.",
              ]
            : followUpState.label === "scheduled_in_app"
              ? [
                  "",
                  "Follow-up status: Scheduled in Nia COACH.",
                  "Reminder status: No email reminder is currently queued.",
                ]
              : followUpState.label === "completed"
                ? [
                    "",
                    "Follow-up status: Completed.",
                  ]
                : followUpState.label === "dismissed"
                  ? [
                      "",
                      "Follow-up status: Dismissed.",
                    ]
                  : []
    );
  return [
    "Your session summary:",
    summaryText,
    "",
    `Themes: ${normalized.detectedThemes.join(", ")}`,
    `Next step: ${normalized.suggestedNextStep}`,
    `Follow-up flag suggested: ${normalized.flagSuggested ? "Yes" : "No"}`,
    ...followUpLines,
  ].join("\n");
}

export function createSessionFromThread(messages = [], meta = {}) {
  const now = Date.now();
  const structuredSummary = buildStructuredFallbackSummary(messages, { generatedAt: now });
  return {
    id: now,
    createdAt: now,
    client: meta.name || "Unknown",
    title: makeTitle(messages),
    summary: generateSessionSummary(messages),
    sessionSummary: structuredSummary,
    messages,
  };
}
